/**
 * Authored Wind Powered Car STEM Mission pages.
 *
 * This is the nine-phase STEM Mission conversion of the Summer Camp Wind Powered
 * Car Journey. The original Summer Camp Journey remains separate and unchanged.
 *
 * Science model: batteries power a motor, the motor spins a propeller, the
 * propeller pushes air backward, and the air pushes the rover forward.
 *
 * Phases: Prelude -> Wonder -> Explore -> Understand -> Apply -> Test & Improve
 * -> Explain -> Reflect -> Grow. The optional YOU DO Version 3 challenge appears
 * after the Grow completion gate and does not create a tenth guided phase.
 *
 * This file is data/configuration consumed by JourneyPageRenderer. It does not
 * create bespoke React pages.
 */
const WIND_POWERED_CAR_MISSION_PAGES = {
  "wind-car-prelude": {
    "step_id": "wind-car-prelude",
    "title": "Emergency at Green Valley",
    "subtitle": "Enter the Air-Push Rescue Mission",
    "phase": "Prelude",
    "story": "A storm has blocked the normal route inside Green Valley Wildlife Reserve. Rangers have a lightweight medicine package, emergency AA batteries, and a small engineering kit—but no gasoline vehicle can use the damaged path. Your team must design a rover that can cross a clear, flat rescue lane.",
    "self_guided": {
      "title": "Welcome to the Green Valley rescue",
      "say": "The rangers need to move a small medicine package across a safe lane. Your challenge is to figure out what might power the rover without pushing it by hand.",
      "do_now": "Watch or read the Green Valley rescue briefing, then open the ranger’s message and identify the route and cargo requirements. We are not building or powering the rover yet.",
      "help_prompt": "Professor Swan, guide me through this phase one action at a time, using the mission evidence and without giving me the answers or skipping safety checks.",
      "transition_summary": "You opened the Green Valley ranger briefing and learned the rescue goal: move a medicine package across a safe, flat lane. We have not yet decided how a rover should move.",
      "next_preview": "Next, choose a starting motion idea and write what observation could test it.",
      "welcome": "Welcome, Future Engineer! I’m Professor Swan. Green Valley needs a rescue rover. The mission videos and rotatable 3D guides will help you build and investigate it.",
      "materials": [
        "Supplied motor-and-propeller rover kit, including chassis, wheels, motor, propeller, battery holder, and hardware",
        "Specified AA batteries",
        "Clear, flat and obstacle-free test lane",
        "Measuring/recording materials specified in the kit and the assembly guide"
      ],
      "safety": [
        "Follow the kit's propeller, battery, and assembly checks before each powered test. Keep fingers, hair, and loose clothing clear. If a part is damaged, hot or requires excessive force, stop and get help.",
        "Keep power off for assembly, adjustments, and troubleshooting.",
        "Keep hands, hair, clothing, and loose objects away from the propeller; test only on a clear lane."
      ],
      "parent_note": "Professor Swan guides age-appropriate assembly and low-voltage trials; the child leads the investigation. Do not require routine supervision. Offer hands-on help if asked or when a real hazard means a step cannot be completed safely. The rover uses batteries to power its propeller, not outdoor wind."
    },
    "self_guided_block_guidance": {
      "green-valley-story-prelude": {
        "title": "Read the rescue constraints",
        "try_now": "Watch the mission and identify what the rover can and cannot do."
      }
    },
    "blocks": [
      {
        "id": "green-valley-story-prelude",
        "master_teacher_watch": {"button": "Watch the Green Valley rescue mystery", "why": "Ranger Nia needs your help. Discover the problem before opening the kit.", "after": "What do you know, and what do you still need to investigate?"},
        "type": "story_prelude",
        "eyebrow": "BEFORE THE RESCUE MISSION",
        "title": "Enter the Green Valley Emergency",
        "prompt": "Watch what happened after the storm, then read Mission Control’s ranger briefing before beginning the rescue investigation.",
        "video": {
          "title": "Emergency at Green Valley: The Investigator’s Briefing",
          "path": "wind-powered-car/prelude/green_valley_story_v1.mp4",
          "poster": "wind-powered-car/prelude/green_valley_story_poster.jpg",
          "caption": "This story introduces the rescue mystery and mission constraints. It does not reveal what pushes the rover forward."
        },
        "reading": {
          "eyebrow": "SARAS-3D STEM MISSION — AIR-PUSH RESCUE ROVER",
          "title": "Ranger Mission Briefing",
          "content": [
            {
              "type": "paragraph",
              "text": "This briefing was transmitted from Green Valley Mission Control at 8:10 this morning."
            },
            {
              "type": "paragraph",
              "text": "The worst of last night’s storm has passed, but the reserve is not back to normal. Ranger teams have found standing water, broken branches, and damaged service roads across the valley. No people or animals were hurt, but one important route is completely blocked."
            },
            {
              "type": "paragraph",
              "text": "Ranger Nia was carrying a lightweight medicine package to the North Meadow Care Station when she reached the damaged section. A fallen tree covers part of the road, and a deep washout has opened beside it. The ranger vehicle cannot cross. The western trail is also too soft for a heavy vehicle, and walking over the unstable ground is not permitted."
            },
            {
              "type": "paragraph",
              "text": "The medicine is still needed before the evening supply check. The package is sealed, light enough for a small machine to carry, and ready at Mission Control. What is missing is a safe way to move it across the damaged section."
            },
            {
              "type": "paragraph",
              "text": "Mission Control studied the reserve map and found one possible route: a narrow, clear, flat lane beside the damaged road. The lane was once used to test small field equipment. It is firm enough for a lightweight machine, but too narrow for the ranger vehicle."
            },
            {
              "type": "paragraph",
              "text": "In the emergency storage room, Nia found a sealed case labeled GREEN VALLEY PROTOTYPE. Inside were emergency AA batteries and a small engineering kit. The case did not include a finished rescue machine or a set of answers. Instead, a mission card was fastened to the lid."
            },
            {
              "type": "callout",
              "title": "Clue written on the mission card",
              "text": "THE MOTOR WILL NOT TOUCH THE WHEELS."
            },
            {
              "type": "paragraph",
              "text": "The message created more questions than answers. Could storm wind push the rover? Could the batteries power a hidden connection? Could stored energy be released in another way? Ranger Nia reminded the team that the batteries might explain where energy begins, but they do not by themselves explain what pushes the whole rover forward."
            },
            {
              "type": "paragraph",
              "text": "The rangers separated what they knew from what they were only guessing. They knew the package was light. They knew the rescue lane was clear and flat. They knew the rover could not be pushed during the mission. They also knew that the motor would not touch the wheels. Everything else was still a theory."
            },
            {
              "type": "paragraph",
              "text": "Your team has been asked to take over the investigation."
            },
            {
              "type": "paragraph",
              "text": "For now, keep the engineering case closed. Study the storm scene and the written rules. Decide which statements are facts, which are ideas, and what observation or test could separate one explanation from another. Your first theory does not have to be correct, but it must be testable."
            },
            {
              "type": "paragraph",
              "text": "The medicine package is sealed. The rescue lane is ready. The North Meadow Care Station is waiting for your report."
            },
            {
              "type": "callout",
              "title": "Mission Control Note",
              "text": "A rescue design must be tested with evidence, not trusted because it sounds possible."
            },
            {
              "type": "numbered_list",
              "title": "MISSION RULES",
              "emphasize": true,
              "items": [
                "THE ROVER CANNOT BE PUSHED BY HAND.",
                "IT MUST CARRY THE LIGHT MEDICINE PACKAGE.",
                "IT MUST STAY INSIDE THE CLEAR RESCUE LANE.",
                "USE ONLY THE SUPPLIED KIT AND BATTERIES."
              ]
            },
            {
              "type": "question",
              "text": "How could the rover move without being pushed, and what evidence would help your team decide?"
            }
          ]
        }
      },
      {
        "id": "rescue-alert",
        "type": "story_hook",
        "eyebrow": "GREEN VALLEY EMERGENCY",
        "title": "Medicine must cross the rescue lane",
        "body": "The rover cannot be pushed by hand during the mission. It must carry a light package, stay inside the lane, and use only the supplied kit and batteries. The strange clue: its motor will not touch the wheels.",
        "tone": "mystery"
      },
      {
        "id": "rescue-image",
        "type": "image_reveal",
        "title": "Open the ranger’s emergency message",
        "prompt": "Reveal the scene, then identify the most important constraints.",
        "reveal_text": "The route is blocked, so the engineers need a small rover that can move a light medicine package across a safe, flat lane. Emergency batteries are available.",
        "image": {
          "path": "wind-powered-car/step-1/green_valley_rescue_hook.png",
          "alt": "A stormy Green Valley rescue scene with a blocked route and no gasoline available."
        },
        "save_as": "rescue_briefing_revealed"
      },
      {
        "id": "prelude-completion",
        "type": "completion_gate",
        "incomplete_title": "Review the Green Valley emergency",
        "complete_title": "Mission briefing received",
        "incomplete_prompt": "Watch or read the rescue briefing and reveal the ranger’s emergency message.",
        "complete_prompt": "You know the mission constraints. Next, Wonder about what could make the rover move.",
        "rules": [
          {
            "field": "rescue_briefing_revealed",
            "condition": "truthy"
          }
        ]
      }
    ]
  },
  "wind-car-wonder": {
    "step_id": "wind-car-wonder",
    "title": "What Could Make the Rover Move?",
    "subtitle": "Wonder Before You Open the Case",
    "phase": "Wonder",
    "story": "Before inspecting the rover, commit to a testable idea about what could make it move and what evidence would help you decide.",
    "self_guided": {
      "title": "Make a rescue-motion prediction",
      "say": "The rover has batteries and a motor, but the motor does not touch its wheels. How could these facts help you investigate?",
      "do_now": "Choose a possible explanation and write what observation would make you reconsider it.",
      "help_prompt": "Professor Swan, guide me through this phase one action at a time, using the mission evidence and without giving me the answers or skipping safety checks.",
      "transition_summary": "You chose a possible rover mechanism and recorded a motion theory. It is ready for investigation, not yet confirmed.",
      "next_preview": "Next, rotate the 3D rover, inspect its parts, and use the science video to check what the motor actually drives."
    },
    "self_guided_block_guidance": {
      "solution-prediction": {
        "title": "Predict a motion source",
        "try_now": "How might the kit parts move the rover without pushing it by hand?"
      }
    },
    "blocks": [
      {
        "id": "solution-prediction",
        "type": "choice_cards",
        "title": "What might make the rover move?",
        "prompt": "Choose your best current theory. This is a prediction, not a graded answer.",
        "choices": [
          {
            "id": "outside_wind",
            "label": "Outdoor wind might push it"
          },
          {
            "id": "battery_wheels",
            "label": "A battery-powered motor might turn the wheels"
          },
          {
            "id": "battery_propeller",
            "label": "A battery-powered motor might spin a propeller that pushes air"
          },
          {
            "id": "stored_spring",
            "label": "A spring might release stored energy"
          },
          {
            "id": "need_evidence",
            "label": "I need to inspect the rover before deciding"
          }
        ],
        "save_as": "rescue_solution_prediction",
        "required": true
      },
      {
        "id": "initial-theory",
        "type": "evidence_response",
        "title": "Write your opening theory",
        "prompt": "How could a machine move when no person pushes it? Include one clue or test that would help you decide.",
        "placeholder": "My current theory is... I would look for evidence such as...",
        "save_as": "initial_motion_theory",
        "min_length": 20,
        "required": true
      },
      {
        "id": "step1-saras",
        "type": "saras_checkpoint",
        "title": "Open the case with Saras",
        "suggested_questions": [
          "What constraints should our rescue rover meet?",
          "What evidence would show where the rover gets its energy?",
          "How can we test an idea without opening the kit yet?",
          "Can air push an object even though we cannot see air?"
        ],
        "hidden_context": "The student is beginning the Wind Powered Car STEM Mission as a Green Valley rescue engineer. Preserve the mystery. The real kit is battery-powered: a closed circuit powers a motor, the motor spins a propeller, the propeller pushes air backward, and the air pushes the rover forward. Do not reveal that chain yet. Help the student identify constraints, separate an energy source from a pushing mechanism, and propose evidence. Ask one useful question at a time.",
        "reveal_policy": "Do not confirm the final mechanism. Predictions may include outdoor wind, wheel drive, or a propeller; ask what observation would distinguish them."
      },
      {
        "id": "wonder-completion",
        "type": "completion_gate",
        "incomplete_title": "Finish your opening theory",
        "complete_title": "Wonder complete: testable theory recorded",
        "incomplete_prompt": "Choose a starting theory and explain what evidence could test it.",
        "complete_prompt": "You have a testable starting theory. Next, Explore the mystery rover before anyone gives away the answer.",
        "rules": [
          {
            "field": "rescue_solution_prediction",
            "condition": "answered"
          },
          {
            "field": "initial_motion_theory",
            "condition": "min_length",
            "value": 20
          }
        ]
      }
    ]
  },
  "wind-car-explore": {
    "step_id": "wind-car-explore",
    "title": "Inspect the Mystery Rover",
    "subtitle": "The Motor Does Not Touch the Wheels!",
    "phase": "Explore",
    "story": "Mission Control reveals the rover. The motor sits beside a propeller, while the wheels spin on separate axles. There is no gear, belt, or axle connecting the motor to the wheels. Study the evidence before watching the explanation.",
    "self_guided": {
      "title": "Inspect the motor-and-propeller rover",
      "say": "This is a battery-powered motor-and-propeller rover, not a car that depends on outdoor wind. Use what you can see to revise your ideas.",
      "do_now": "Explore the virtual rover, watch the science evidence, inspect the motor connection, and update your theory with visible clues.",
      "help_prompt": "Professor Swan, guide me through this phase one action at a time, using the mission evidence and without giving me the answers or skipping safety checks.",
      "transition_summary": "You recorded clues from the rover model and updated your motion theory. In the next phase, investigate the battery, motor, and moving air to test your explanation.",
      "next_preview": "Next, trace battery power through the motor and investigate how moving air pushes the rover."
    },
    "self_guided_block_guidance": {
      "mainmenu-pwo-block": {
        "title": "Inspect the virtual machine",
        "try_now": "Find the motor, propeller, and wheels before explaining how it moves."
      },
      "motor-job-check": {
        "title": "Examine the motor shaft",
        "try_now": "Look for what touches the rotating shaft. Use the 3D model or video to check before choosing."
      }
    },
    "blocks": [
      {
        "id": "step2-story",
        "type": "story_hook",
        "eyebrow": "MECHANISM MYSTERY",
        "title": "What is connected—and what is not?",
        "body": "A good engineer does not name a force before observing the machine. Find the motor, propeller, battery holder, wheels, and axles. Then look for the missing connection between the motor and wheels.",
        "tone": "mystery"
      },
      {
        "id": "rover-image-gallery",
        "type": "image_gallery",
        "title": "Inspect the visible clues",
        "prompt": "Compare the 3D rover view with the mechanism clue diagram.",
        "save_as": "rover_image_examined",
        "images": [
          {
            "id": "pwo-mainmenu",
            "label": "3D rover overview",
            "path": "wind-powered-car/step-2/wind_car_mainmenu.png",
            "caption": "The PWO overview shows the completed rover and its learning mission.",
            "alt": "Wind Powered Car PWO main menu showing the completed propeller rover."
          },
          {
            "id": "no-wheel-drive",
            "label": "Motor-to-wheel clue",
            "path": "wind-powered-car/step-2/motor_not_connected_to_wheels.png",
            "caption": "The motor turns the propeller; it is not mechanically connected to the wheels.",
            "self_guided_caption": "Look for a connection from the motor shaft. Compare the propeller and the wheels before drawing your conclusion.",
            "alt": "Diagram showing a motor and propeller with no connection to the rover wheels."
          }
        ]
      },
      {
        "id": "mainmenu-pwo-block",
        "type": "pwo_asset",
        "title": "Explore the 3D Mystery Rover",
        "mission": "Rotate and inspect the rover. Identify the motor, propeller, battery holder, wheels, and the missing motor-to-wheel connection.",
        "save_as": "mainmenu_pwo_opened",
        "asset": {
          "id": "mainmenu-pwo",
          "type": "saras_pwo",
          "asset_ref": {
            "source_pwo_id": 1166,
            "scene_name": "mainmenu"
          },
          "provenance": {
            "source_type": "collection",
            "collection_id": 168,
            "collection_item_id": 143
          },
          "content_context": {
            "base_board": "saras",
            "language_id": 1,
            "board_topic_id": 934,
            "source_topic_id": 960
          }
        }
      },
      {
        "id": "rover-observations",
        "type": "multi_select",
        "title": "Select the clues you can verify",
        "prompt": "Choose at least two observations that are visible in the rover.",
        "choices": [
          {
            "id": "motor_propeller",
            "label": "The motor shaft connects to the propeller"
          },
          {
            "id": "wheels_axles",
            "label": "The wheels sit on their own axles"
          },
          {
            "id": "no_gear",
            "label": "No gear or belt connects the motor to the wheels"
          },
          {
            "id": "battery_holder",
            "label": "The battery holder connects to the motor with wires"
          },
          {
            "id": "propeller_rear",
            "label": "The propeller is positioned to move air behind the rover"
          },
          {
            "id": "motor_drives_wheels",
            "label": "The motor shaft directly turns a wheel"
          }
        ],
        "save_as": "rover_visual_observations",
        "min_selections": 2,
        "required": true
      },
      {
        "id": "motion-source",
        "type": "choice_cards",
        "title": "What probably pushes the whole rover?",
        "prompt": "Commit to a prediction before the video.",
        "choices": [
          {
            "id": "wheel_drive",
            "label": "The motor secretly drives the wheels"
          },
          {
            "id": "air_push",
            "label": "The propeller pushes air, and the air pushes the rover"
          },
          {
            "id": "outside_wind",
            "label": "Outdoor wind turns the propeller and motor"
          },
          {
            "id": "vibration",
            "label": "Motor vibration shakes the rover forward"
          },
          {
            "id": "uncertain",
            "label": "I need the science evidence"
          }
        ],
        "save_as": "motion_source_prediction",
        "required": true
      },
      {
        "id": "science-rover-video-block",
        "master_teacher_watch": {"button": "Watch the rover science clue", "why": "Look for evidence about how this rover moves."},
        "type": "video_asset",
        "title": "The Science of the Wind Powered Rover",
        "mission": "Watch after making your prediction. Look for what the motor turns and what the wheels do.",
        "save_as": "science_rover_video_opened",
        "asset": {
          "id": "science-rover-video",
          "type": "saras_video",
          "asset_ref": {
            "source_video_id": 7040,
            "tag_id": 30057
          },
          "provenance": {
            "source_type": "collection",
            "collection_id": 168,
            "collection_item_id": 143
          },
          "content_context": {
            "base_board": "saras",
            "language_id": 1,
            "board_topic_id": 934,
            "source_topic_id": 960
          }
        }
      },
      {
        "id": "motor-job-check",
        "type": "quiz_question",
        "title": "Evidence check",
        "prompt": "Which part is directly attached to the motor shaft in this kit?",
        "choices": [
          {
            "id": "wheel",
            "label": "A wheel"
          },
          {
            "id": "propeller",
            "label": "The propeller"
          },
          {
            "id": "axle",
            "label": "A wheel axle"
          },
          {
            "id": "battery",
            "label": "The battery"
          }
        ],
        "correct_answer": "propeller",
        "save_as": "motor_connection_check",
        "correct_feedback": "Correct. The motor turns the propeller. The wheels remain free to roll.",
        "incorrect_feedback": "Inspect the shaft in the PWO and video. The motor shaft does not reach the wheels.",
        "self_guided_incorrect_feedback": "Examine the motor shaft again in the 3D model or video. Which part touches it and turns with it? Use that observation before trying again.",
        "self_guided_hint_steps": [
          "Try rotating the 3D rover to view the motor from the side. Follow the shaft toward its tip.",
          "Pause the video when the motor is visible. Compare what turns with the motor and what rolls on the ground.",
          "Point to the end of the motor shaft. Describe what touches it, even if you don't know its name.",
          "Tell me one thing you actually saw at the end of the shaft. We can compare your observation with the choices together."
        ]
      },
      {
        "id": "step2-explanation",
        "type": "evidence_response",
        "title": "Revise the motion theory",
        "prompt": "Use at least two visible clues. Why can the wheels roll even though the motor does not turn them?",
        "self_guided_prompt": "Describe two things you actually observed about the moving parts. How do those clues change your original idea about the rover?",
        "placeholder": "The motor is connected to... The wheels are not connected to... I now think...",
        "self_guided_placeholder": "I observed... Another clue was... I now think...",
        "save_as": "revised_motion_theory",
        "min_length": 30,
        "required": true
      },
      {
        "id": "step2-saras",
        "type": "saras_checkpoint",
        "title": "Interrogate the rover with Saras",
        "suggested_questions": [
          "What evidence proves the motor does not drive the wheels?",
          "What is the motor connected to?",
          "Why can unpowered wheels still roll?",
          "Can you challenge my motion-source prediction?"
        ],
        "self_guided_suggested_questions": [
          "What should I inspect to test my choice?",
          "How can I compare the motor and the moving parts?",
          "Can you challenge my prediction without telling me the answer?"
        ],
        "hidden_context": "The student is inspecting the battery-powered propeller rover. Use rover_visual_observations, motion_source_prediction, motor_connection_check, and revised_motion_theory. The motor directly turns the propeller, not the wheels. The wheels are passive rollers. Help the student use visual evidence. You may now explain that the propeller moves air, but save the full backward-air/forward-thrust explanation for the Understand phase.",
        "reveal_policy": "Reveal the motor-to-propeller connection and passive wheel role. Do not yet deliver a formal action-reaction lesson.",
        "self_guided_reveal_policy": "While the motor-connection evidence check is unfinished, help the learner inspect parts without naming or eliminating answer choices, even when asked directly. After they complete that check, help compare their observation and earlier prediction. Save the full action-reaction explanation for Understand."
      },
      {
        "id": "explore-completion",
        "type": "completion_gate",
        "incomplete_title": "Complete the rover inspection",
        "complete_title": "Explore complete: mechanism clue verified",
        "incomplete_prompt": "Open the 3D model, select visible clues, make a prediction, and explain what changed.",
        "complete_prompt": "You proved that the motor does not drive the wheels. Next, Understand where the energy begins and how moving air creates thrust.",
        "self_guided_complete_prompt": "You recorded your inspection and revised your theory. Next, investigate the rover’s energy and how moving air can create motion.",
        "self_guided_complete_title": "Explore complete: your clues are recorded",
        "rules": [
          {
            "field": "mainmenu_pwo_opened",
            "condition": "truthy"
          },
          {
            "field": "rover_visual_observations",
            "condition": "min_items",
            "value": 2
          },
          {
            "field": "motion_source_prediction",
            "condition": "answered"
          },
          {
            "field": "science_rover_video_opened",
            "condition": "truthy"
          },
          {
            "field": "motor_connection_check",
            "condition": "equals",
            "value": "propeller"
          },
          {
            "field": "revised_motion_theory",
            "condition": "min_length",
            "value": 30
          }
        ]
      }
    ]
  },
  "wind-car-understand": {
    "step_id": "wind-car-understand",
    "title": "Trace the Power and Push",
    "subtitle": "Where Energy Begins and How Air Creates Thrust",
    "phase": "Understand",
    "story": "Connect the rover’s battery, circuit, motor, propeller, moving air, and rolling wheels into one causal explanation, then use evidence to solve the backward-push mystery.",
    "self_guided": {
      "title": "Trace the power and the push",
      "say": "Follow the energy from the batteries to the motor and into moving air. Make a prediction before seeing the explanation.",
      "do_now": "Inspect parts, trace the complete circuit, predict the direction of airflow, and support your push explanation with the available demonstrations.",
      "help_prompt": "Professor Swan, guide me through this phase one action at a time, using the mission evidence and without giving me the answers or skipping safety checks.",
      "transition_summary": "You matched the rover parts to their jobs, traced the power path, and used demonstrations and reasoning checks to connect airflow with thrust.",
      "next_preview": "Next, open the installation guide, inventory the kit, and build the rover with its power disconnected."
    },
    "self_guided_block_guidance": {
      "power-path": {
        "title": "Build an energy chain",
        "try_now": "Trace the supplied power through the motor and consider what moves the air."
      },
      "accessories-pwo-block": {
        "title": "Inspect motor and propeller parts",
        "try_now": "Rotate the 3D components and describe which parts are electrical, which move, and what they are connected to."
      }
    },
    "blocks": [
      {
        "id": "step3-story",
        "type": "story_hook",
        "eyebrow": "POWER PATH LAB",
        "title": "A battery is the beginning, not the whole explanation",
        "body": "The battery stores chemical energy. A complete circuit transfers electrical energy to the motor. The motor creates rotation, the propeller moves air, and the rover rolls. Your job is to connect every link.",
        "tone": "mystery"
      },
      {
        "id": "accessories-pwo-block",
        "type": "pwo_asset",
        "title": "Open the Virtual Parts Tray",
        "mission": "Click the parts, learn their names, and compare them with your real kit. Keep the real batteries out.",
        "save_as": "accessories_pwo_opened",
        "asset": {
          "id": "accessories-pwo",
          "type": "saras_pwo",
          "asset_ref": {
            "source_pwo_id": 1166,
            "scene_name": "requiredaccessories"
          },
          "provenance": {
            "source_type": "collection",
            "collection_id": 168,
            "collection_item_id": 143
          },
          "content_context": {
            "base_board": "saras",
            "language_id": 1,
            "board_topic_id": 934,
            "source_topic_id": 960
          }
        }
      },
      {
        "id": "parts-gallery",
        "type": "image_gallery",
        "title": "Compare the virtual and real kit",
        "prompt": "Use shape, wires, holes, shafts, and connection points as evidence.",
        "save_as": "parts_image_examined",
        "images": [
          {
            "id": "virtual-parts",
            "label": "Virtual parts tray",
            "path": "wind-powered-car/step-3/wind_car_accessories.png",
            "caption": "The PWO organizes the chassis, motor, battery holder, propeller, wheels, axles, brackets, and hardware.",
            "alt": "Virtual Wind Powered Car parts arranged on a wooden table."
          },
          {
            "id": "actual-kit",
            "label": "Actual physical kit",
            "path": "wind-powered-car/step-3/actual_kit_inventory.png",
            "caption": "The actual kit includes the chassis, DC motor, blue propeller, green wheels, metal axles and brackets, battery holder with switch, tape, and hardware.",
            "alt": "Actual Wind Powered Car kit components laid out on a table."
          },
          {
            "id": "energy-path",
            "label": "Energy path diagram",
            "path": "wind-powered-car/step-3/battery_to_rover_energy_path.png",
            "caption": "Every link must work: battery, circuit, motor, propeller, moving air, and rolling rover.",
            "alt": "Child-friendly diagram of energy and motion from battery to rover."
          }
        ]
      },
      {
        "id": "parts-found",
        "type": "multi_select",
        "title": "Inventory the real kit",
        "prompt": "Check a part only after you find it. Keep batteries out during this investigation.",
        "choices": [
          {
            "id": "chassis",
            "label": "Blue chassis/frame"
          },
          {
            "id": "motor",
            "label": "Small DC motor"
          },
          {
            "id": "propeller",
            "label": "Blue propeller"
          },
          {
            "id": "battery_holder",
            "label": "Battery holder with red/black wires and switch"
          },
          {
            "id": "wheels",
            "label": "Four wheels"
          },
          {
            "id": "axles",
            "label": "Two metal axles"
          },
          {
            "id": "angle_brackets",
            "label": "Four axle angle brackets"
          },
          {
            "id": "motor_bracket",
            "label": "U-shaped motor bracket"
          },
          {
            "id": "tape",
            "label": "Double-sided tape"
          },
          {
            "id": "hardware",
            "label": "Screws, nuts, spacers, and small hardware"
          }
        ],
        "save_as": "component_inventory",
        "min_selections": 8,
        "required": true
      },
      {
        "id": "component-jobs",
        "type": "matching",
        "title": "Match each part to its main job",
        "prompt": "Choose the strongest match. Later, several parts will cooperate as one system.",
        "save_as": "component_job_classification",
        "items": [
          {
            "id": "battery",
            "label": "Battery holder and batteries",
            "options": [
              {
                "id": "store_supply",
                "label": "Store chemical energy and supply electrical energy"
              },
              {
                "id": "push_air",
                "label": "Directly push air backward"
              },
              {
                "id": "reduce_friction",
                "label": "Reduce wheel rubbing"
              }
            ]
          },
          {
            "id": "switch_wires",
            "label": "Switch and wires",
            "options": [
              {
                "id": "control_path",
                "label": "Connect and control the electrical path"
              },
              {
                "id": "store_energy",
                "label": "Store all the chemical energy"
              },
              {
                "id": "roll",
                "label": "Roll on the floor"
              }
            ]
          },
          {
            "id": "motor",
            "label": "Motor",
            "options": [
              {
                "id": "electrical_to_rotation",
                "label": "Convert electrical energy into rotation"
              },
              {
                "id": "outdoor_wind",
                "label": "Collect outdoor wind energy"
              },
              {
                "id": "hold_axles",
                "label": "Hold the wheel axles straight"
              }
            ]
          },
          {
            "id": "propeller",
            "label": "Propeller",
            "options": [
              {
                "id": "push_air",
                "label": "Push air backward when it spins"
              },
              {
                "id": "power_battery",
                "label": "Recharge the batteries"
              },
              {
                "id": "turn_wheels",
                "label": "Directly turn the wheels through gears"
              }
            ]
          },
          {
            "id": "wheels_axles",
            "label": "Wheels and axles",
            "options": [
              {
                "id": "roll_smoothly",
                "label": "Let the rover roll with less rubbing"
              },
              {
                "id": "make_current",
                "label": "Create electric current"
              },
              {
                "id": "spin_propeller",
                "label": "Spin the propeller"
              }
            ]
          },
          {
            "id": "chassis",
            "label": "Chassis and brackets",
            "options": [
              {
                "id": "support_align",
                "label": "Support and align the rover parts"
              },
              {
                "id": "make_thrust",
                "label": "Create thrust without a propeller"
              },
              {
                "id": "store_charge",
                "label": "Store electrical charge"
              }
            ]
          }
        ],
        "required": true
      },
      {
        "id": "system-video-block",
        "master_teacher_watch": {"button": "Watch how the rover works", "why": "Watch the power path, then investigate your own kit."},
        "type": "video_asset",
        "title": "The Wind Powered Rover",
        "mission": "Watch for the full path from the battery to rover motion. Ignore advanced vocabulary and focus on what each part does.",
        "save_as": "system_video_opened",
        "asset": {
          "id": "system-video",
          "type": "saras_video",
          "asset_ref": {
            "source_video_id": 7039,
            "tag_id": 30056
          },
          "provenance": {
            "source_type": "collection",
            "collection_id": 168,
            "collection_item_id": 143
          },
          "content_context": {
            "base_board": "saras",
            "language_id": 1,
            "board_topic_id": 934,
            "source_topic_id": 960
          }
        }
      },
      {
        "id": "power-path",
        "type": "sequence_builder",
        "title": "Arrange the rover’s energy-and-motion path",
        "prompt": "Start with stored energy and end with the rolling rover.",
        "save_as": "power_path_sequence",
        "items": [
          {
            "id": "battery",
            "label": "Chemical energy is stored in the batteries"
          },
          {
            "id": "circuit",
            "label": "A closed circuit transfers electrical energy"
          },
          {
            "id": "motor",
            "label": "The motor changes electrical energy into rotation"
          },
          {
            "id": "propeller",
            "label": "The rotating propeller pushes air backward"
          },
          {
            "id": "air_reaction",
            "label": "The air pushes the propeller and rover forward"
          },
          {
            "id": "rolling",
            "label": "Free wheels let the rover roll"
          }
        ],
        "correct_order": [
          "battery",
          "circuit",
          "motor",
          "propeller",
          "air_reaction",
          "rolling"
        ],
        "correct_feedback": "Excellent. You connected the energy source, circuit, converter, air-push, and rolling motion.",
        "retry_feedback": "Ask what must happen before the motor spins, and what the propeller must do before the rover moves.",
        "required": true
      },
      {
        "id": "closed-circuit-check",
        "type": "quiz_question",
        "title": "Circuit check",
        "prompt": "What happens if one wire is disconnected?",
        "choices": [
          {
            "id": "faster",
            "label": "The propeller spins faster"
          },
          {
            "id": "stop",
            "label": "The circuit opens and the motor stops"
          },
          {
            "id": "same",
            "label": "Nothing changes"
          },
          {
            "id": "wheels_drive",
            "label": "The wheels begin driving the motor"
          }
        ],
        "correct_answer": "stop",
        "save_as": "closed_circuit_check",
        "correct_feedback": "Correct. A broken path is an open circuit, so electrical energy cannot reach the motor.",
        "incorrect_feedback": "Trace the path from one battery terminal through the motor and back. A gap breaks the loop."
      },
      {
        "id": "power-explanation",
        "type": "evidence_response",
        "title": "Explain where motion begins",
        "prompt": "Explain why the battery alone is not enough. Include the circuit, motor, and propeller.",
        "placeholder": "Energy begins in... The circuit... The motor... The propeller...",
        "save_as": "closed_circuit_explanation",
        "min_length": 40,
        "required": true
      },
      {
        "id": "step3-saras",
        "type": "saras_checkpoint",
        "title": "Audit the power path with Saras",
        "suggested_questions": [
          "Does a battery store electricity or chemical energy?",
          "Why must the circuit be complete?",
          "What energy change happens in the motor?",
          "Can you check my sequence without giving me the order?"
        ],
        "hidden_context": "The student is tracing the Wind Powered Car power path. Use component_inventory, component_job_classification, power_path_sequence, closed_circuit_check, and closed_circuit_explanation. Teach the age-appropriate chain: chemical energy in batteries, electrical energy through a closed circuit, motor rotation, propeller pushes air backward, air pushes rover forward, wheels roll. Do not imply that outside wind powers the motor. Avoid equations and electron-level detail.",
        "reveal_policy": "The full high-level energy chain may now be taught. Save the deeper direction-of-force investigation for the second half of Understand."
      },
      {
        "id": "step4-story",
        "type": "story_hook",
        "eyebrow": "DIRECTION ALERT",
        "title": "The propeller pushes one way; the rover moves the other",
        "body": "Before using the word thrust, predict the direction of the airflow. Then use a safe fan-and-paper, balloon, or teacher-controlled propeller demonstration to collect evidence.",
        "tone": "mystery"
      },
      {
        "id": "air-diagram",
        "type": "image_gallery",
        "title": "Read the direction arrows",
        "prompt": "The arrows are a model. Use the demonstration or video as the evidence.",
        "save_as": "air_push_diagram_examined",
        "images": [
          {
            "id": "air-push",
            "label": "Backward air, forward rover",
            "path": "wind-powered-car/step-4/air_push_direction.png",
            "caption": "The propeller accelerates air backward. The air pushes back on the propeller and rover, producing forward thrust.",
            "alt": "Diagram showing air pushed backward and the rover pushed forward."
          }
        ]
      },
      {
        "id": "air-prediction",
        "type": "choice_cards",
        "title": "Which way should the propeller send the air?",
        "prompt": "Choose before watching or running the demonstration.",
        "choices": [
          {
            "id": "backward",
            "label": "Backward, opposite the desired rover motion"
          },
          {
            "id": "forward",
            "label": "Forward, in the same direction as the rover"
          },
          {
            "id": "up",
            "label": "Upward only"
          },
          {
            "id": "no_air",
            "label": "The propeller should not move air"
          }
        ],
        "save_as": "air_direction_prediction",
        "required": true
      },
      {
        "id": "backward-push-video-block",
        "master_teacher_watch": {"button": "Watch the backward push puzzle", "why": "Watch for which way air moves and what the rover does."},
        "type": "video_asset",
        "title": "The Backward Push Paradox",
        "mission": "Optional enrichment after your prediction. Focus on the direction of the air and rover; ask Saras about unfamiliar words.",
        "asset": {
          "id": "backward-push-video",
          "type": "saras_video",
          "asset_ref": {
            "source_video_id": 7042,
            "tag_id": 30059
          },
          "provenance": {
            "source_type": "collection",
            "collection_id": 168,
            "collection_item_id": 143
          },
          "content_context": {
            "base_board": "saras",
            "language_id": 1,
            "board_topic_id": 934,
            "source_topic_id": 960
          }
        }
      },
      {
        "id": "balloon-pwo-block",
        "type": "pwo_asset",
        "title": "Optional Action–Reaction Exploration",
        "mission": "Use the balloon animation only if it is clear and age-appropriate in your local build. Compare escaping air with the rover propeller.",
        "asset": {
          "id": "balloon-pwo",
          "type": "saras_pwo",
          "asset_ref": {
            "source_pwo_id": 524,
            "scene_name": "anim_balloon"
          },
          "provenance": {
            "source_type": "collection",
            "collection_id": 168,
            "collection_item_id": 140
          },
          "content_context": {
            "base_board": "saras",
            "language_id": 1,
            "board_topic_id": 740,
            "source_topic_id": 562
          }
        }
      },
      {
        "id": "demo-observation",
        "type": "evidence_response",
        "title": "Record what the moving air did",
        "prompt": "Describe evidence from the paper, balloon, fan, PWO, or teacher-controlled propeller demonstration.",
        "placeholder": "When air moved toward..., the paper/balloon/rover...",
        "save_as": "air_push_observation",
        "min_length": 20,
        "required": true
      },
      {
        "id": "thrust-check",
        "type": "quiz_question",
        "title": "Direction check",
        "prompt": "The rover needs to move forward. Which statement best explains the air-push?",
        "choices": [
          {
            "id": "air_back_rover_forward",
            "label": "The propeller pushes air backward, and the air pushes the rover forward"
          },
          {
            "id": "air_forward_rover_forward",
            "label": "The propeller pushes air forward, pulling the rover forward"
          },
          {
            "id": "motor_wheels",
            "label": "The motor turns hidden wheel gears"
          },
          {
            "id": "outside_wind",
            "label": "Outdoor wind powers the motor"
          }
        ],
        "correct_answer": "air_back_rover_forward",
        "save_as": "thrust_direction_check",
        "correct_feedback": "Yes. The forward push on the rover is called thrust or air-push.",
        "incorrect_feedback": "Use the arrows and your observation: compare the air direction with the rover direction."
      },
      {
        "id": "reverse-wire-check",
        "type": "quiz_question",
        "title": "Prediction: reverse the motor direction",
        "prompt": "If the motor wires are swapped and the propeller spins the opposite way, what may change?",
        "choices": [
          {
            "id": "air_direction",
            "label": "The direction of airflow and thrust may reverse"
          },
          {
            "id": "battery_size",
            "label": "The batteries become larger"
          },
          {
            "id": "wheel_count",
            "label": "The rover gains more wheels"
          },
          {
            "id": "no_change",
            "label": "Nothing about the propeller changes"
          }
        ],
        "correct_answer": "air_direction",
        "save_as": "reverse_motor_direction_check",
        "correct_feedback": "Correct. Reversing a DC motor can reverse propeller rotation and airflow direction.",
        "incorrect_feedback": "Think about what the motor controls directly: the direction the propeller spins.",
        "self_guided_incorrect_feedback": "Compare the motor and propeller in the model. If the motor direction changes, what might you expect to observe?"
      },
      {
        "id": "backward-explanation",
        "type": "evidence_response",
        "title": "Resolve the paradox",
        "prompt": "Explain how pushing air backward can make the rover go forward. Use the words propeller, air, and push or thrust.",
        "placeholder": "The propeller... The air... Therefore the rover...",
        "save_as": "backward_push_explanation",
        "min_length": 35,
        "required": true
      },
      {
        "id": "step4-saras",
        "type": "saras_checkpoint",
        "title": "Defend the direction model with Saras",
        "suggested_questions": [
          "Why does backward-moving air help the rover go forward?",
          "What evidence should I use instead of just saying “thrust”?",
          "What might happen if the propeller spins the other direction?",
          "How is a balloon similar to this rover?"
        ],
        "hidden_context": "The student is investigating child-friendly action and reaction. Use air_direction_prediction, air_push_observation, thrust_direction_check, reverse_motor_direction_check, and backward_push_explanation. Explain that the propeller pushes air backward and the air pushes back on the propeller/rover, producing forward thrust. Avoid formal vector notation. Do not call the rover outdoor-wind powered.",
        "reveal_policy": "The backward-air/forward-thrust relationship may now be fully explained in age-appropriate language."
      },
      {
        "id": "understand-completion",
        "type": "completion_gate",
        "incomplete_title": "Complete the power-and-thrust investigation",
        "complete_title": "Understand complete: power path and thrust explained",
        "incomplete_prompt": "Trace the parts and closed circuit, then use direction evidence to explain why pushing air backward moves the rover forward.",
        "complete_prompt": "You can explain where the rover’s energy begins and how the propeller creates forward thrust. Next, Apply the science to a safe build.",
        "rules": [
          {
            "field": "accessories_pwo_opened",
            "condition": "truthy"
          },
          {
            "field": "component_inventory",
            "condition": "min_items",
            "value": 8
          },
          {
            "field": "component_job_classification",
            "condition": "completed"
          },
          {
            "field": "system_video_opened",
            "condition": "truthy"
          },
          {
            "field": "power_path_sequence",
            "condition": "sequence_correct"
          },
          {
            "field": "closed_circuit_check",
            "condition": "equals",
            "value": "stop"
          },
          {
            "field": "closed_circuit_explanation",
            "condition": "min_length",
            "value": 40
          },
          {
            "field": "air_direction_prediction",
            "condition": "answered"
          },
          {
            "field": "air_push_observation",
            "condition": "min_length",
            "value": 20
          },
          {
            "field": "thrust_direction_check",
            "condition": "equals",
            "value": "air_back_rover_forward"
          },
          {
            "field": "reverse_motor_direction_check",
            "condition": "equals",
            "value": "air_direction"
          },
          {
            "field": "backward_push_explanation",
            "condition": "min_length",
            "value": 35
          }
        ]
      }
    ]
  },
  "wind-car-apply": {
    "step_id": "wind-car-apply",
    "title": "Build the Air-Push Rescue Rover",
    "subtitle": "Prepare, Build, Pause, Check",
    "phase": "Apply",
    "story": "Turn the science plan into a safe physical rover: verify the kit, predict likely failure points, build in checked stages, and keep power disconnected during every adjustment.",
    "self_guided": {
      "title": "Build the rescue rover safely",
      "say": "Before using power, a careful engineer checks the kit, the wheels, the propeller, and the route. Use the authored assembly guide with Professor Swan helping you step by step.",
      "do_now": "Inventory the parts, complete the power-off and safety checks, rotate the virtual installation, and follow the rover assembly tracker. Ask Professor Swan about any difficult step.",
      "help_prompt": "Professor Swan, guide me through this phase one action at a time, using the mission evidence and without giving me the answers or skipping safety checks.",
      "transition_summary": "You matched the physical parts to the guide, opened the 3D installation sequence, completed the assembly checks, and checked that the wheels can spin freely.",
      "next_preview": "Next, record the first powered spin and measured run before changing one design variable."
    },
    "self_guided_block_guidance": {
      "safety-checks": {
        "title": "Complete the propeller safety check",
        "try_now": "Finish the authored kit-readiness checks while power is off. Ask Professor Swan about any unclear item."
      },
      "installation-pwo-block": {
        "title": "Use the 3D installation stages",
        "try_now": "Open the rotatable installation PWO and inspect each stage before making the same physical assembly step, with power off, asking Professor Swan for guidance when needed."
      }
    },
    "blocks": [
      {
        "id": "step5-story",
        "type": "story_hook",
        "eyebrow": "BUILD AUTHORIZATION",
        "title": "No batteries until the mechanical checks are complete",
        "body": "Lay out the kit, count the parts, and keep power off. Engineers prevent problems by inspecting before assembling.",
        "tone": "mystery"
      },
      {
        "id": "readiness-gallery",
        "type": "image_gallery",
        "title": "Inspect the actual kit and guide",
        "prompt": "Use the real kit image for inventory and the printed guide as a teacher/reference check.",
        "save_as": "readiness_image_examined",
        "images": [
          {
            "id": "actual-kit",
            "label": "Actual kit inventory",
            "path": "wind-powered-car/step-5/actual_kit_inventory.png",
            "caption": "Find the chassis, motor, propeller, wheels, axles, angle brackets, motor bracket, battery holder, tape, and hardware.",
            "alt": "Actual Wind Powered Car kit parts on a table."
          },
          {
            "id": "build-guide",
            "label": "Physical build guide",
            "path": "wind-powered-car/step-5/physical_build_guide.png",
            "caption": "The printed guide confirms the battery holder, wire direction, wheel spacing, propeller attachment, and switch.",
            "alt": "Printed Wind Powered Vehicle build guide with steps three through six."
          }
        ]
      },
      {
        "id": "parts-readiness",
        "type": "multi_select",
        "title": "Confirm the mission-critical parts",
        "prompt": "Select every part you have located. If something is missing, tell Swan rather than substituting unknown hardware.",
        "choices": [
          {
            "id": "chassis",
            "label": "Chassis/frame"
          },
          {
            "id": "four_brackets",
            "label": "Four axle angle brackets"
          },
          {
            "id": "two_axles",
            "label": "Two straight axles"
          },
          {
            "id": "four_wheels",
            "label": "Four wheels"
          },
          {
            "id": "motor_bracket",
            "label": "Motor bracket"
          },
          {
            "id": "motor",
            "label": "DC motor"
          },
          {
            "id": "propeller",
            "label": "Propeller"
          },
          {
            "id": "battery_holder",
            "label": "Battery holder with switch and wires"
          },
          {
            "id": "tape",
            "label": "Double-sided tape"
          },
          {
            "id": "screws",
            "label": "Required screws and small hardware"
          }
        ],
        "save_as": "parts_readiness_check",
        "min_selections": 9,
        "required": true
      },
      {
        "id": "safety-checks",
        "type": "build_checklist",
        "title": "Team safety authorization",
        "prompt": "Every item must be completed before construction.",
        "save_as": "safety_readiness_check",
        "statuses": [
          {
            "id": "complete",
            "label": "Confirmed"
          },
          {
            "id": "help",
            "label": "Need teacher help"
          },
          {
            "id": "not_yet",
            "label": "Not yet"
          }
        ],
        "items": [
          {
            "id": "power_off",
            "label": "Batteries stay out and the switch stays OFF during assembly and adjustment",
            "help": "Power is added only for a brief, teacher-approved test."
          },
          {
            "id": "propeller_space",
            "label": "Fingers, faces, hair, and loose clothing will stay away from the propeller",
            "help": "A spinning propeller can catch or strike objects."
          },
          {
            "id": "clear_floor_lane",
            "label": "The rover will be tested on the floor in a clear lane",
            "help": "Do not launch it from a table or toward people."
          },
          {
            "id": "stop_heat_smell",
            "label": "We will stop for a hot battery, damaged wire, unusual smell, or damaged motor",
            "help": "If electrical parts are damaged, leave power off and get adult help instead of touching them."
          },
          {
            "id": "no_liquids",
            "label": "We will not use oil, soap, water, or unapproved liquids",
            "help": "Use alignment and spacing—not liquids—to reduce rubbing."
          },
          {
            "id": "power_off_adjust",
            "label": "Power will be disconnected before touching wheels, wires, motor, or propeller",
            "help": "Observe first, switch off, then adjust."
          }
        ]
      },
      {
        "id": "failure-prediction",
        "type": "choice_cards",
        "title": "Which problem is most likely to stop your first run?",
        "prompt": "Choose one to watch closely during the build.",
        "choices": [
          {
            "id": "loose_wire",
            "label": "A loose or incorrect electrical connection"
          },
          {
            "id": "wheel_rub",
            "label": "A wheel rubbing against the frame"
          },
          {
            "id": "crooked_axle",
            "label": "A crooked axle causing drag or turning"
          },
          {
            "id": "blocked_propeller",
            "label": "A blocked or backward-facing propeller"
          },
          {
            "id": "missing_part",
            "label": "A missing or incorrectly placed part"
          }
        ],
        "save_as": "failure_cause_prediction",
        "required": true
      },
      {
        "id": "build-plan-note",
        "type": "evidence_response",
        "title": "Write the team’s prevention plan",
        "prompt": "How will your team prevent or detect the problem you selected?",
        "placeholder": "We will check... before...",
        "save_as": "readiness_prevention_plan",
        "min_length": 20,
        "required": true
      },
      {
        "id": "step5-saras",
        "type": "saras_checkpoint",
        "title": "Run the readiness review with Saras",
        "suggested_questions": [
          "Which parts should I count before building?",
          "How can I tell whether an axle is straight?",
          "Why must the batteries stay out during assembly?",
          "Help me improve our failure-prevention plan."
        ],
        "hidden_context": "The student is preparing to build the battery-powered propeller rover. Use parts_readiness_check, safety_readiness_check, failure_cause_prediction, and readiness_prevention_plan. Help identify parts and safe checks. Do not invent replacement parts or tell the student to use oil, soap, or liquids. Do not provide unverified assembly details; point the learner to the PWO and physical guide later in this Apply phase.",
        "reveal_policy": "Part names, safety rules, and likely failure modes may be discussed. Assembly order belongs in the guided-build portion of this Apply phase."
      },
      {
        "id": "step6-story",
        "type": "story_hook",
        "eyebrow": "ASSEMBLY BAY",
        "title": "Build with power disconnected",
        "body": "The physical guide shows six major stages, while the 3D PWO lets you revisit individual installation steps. Use both. If the hardware differs from the picture, stop and ask the teacher rather than forcing a part.",
        "tone": "mystery"
      },
      {
        "id": "installation-pwo-block",
        "type": "pwo_asset",
        "title": "Open the 3D Installation Guide",
        "mission": "Follow the installation scene one step at a time. Use the numbered buttons to revisit a stage before changing the real build.",
        "save_as": "installation_pwo_opened",
        "asset": {
          "id": "installation-pwo",
          "type": "saras_pwo",
          "asset_ref": {
            "source_pwo_id": 1166,
            "scene_name": "installation"
          },
          "provenance": {
            "source_type": "collection",
            "collection_id": 168,
            "collection_item_id": 143
          },
          "content_context": {
            "base_board": "saras",
            "language_id": 1,
            "board_topic_id": 934,
            "source_topic_id": 960
          }
        }
      },
      {
        "id": "installation-gallery",
        "type": "image_gallery",
        "title": "Compare the installation references",
        "prompt": "Use the PWO overview, a selected PWO stage, and the physical guide together.",
        "save_as": "installation_reference_examined",
        "images": [
          {
            "id": "pwo-overview",
            "label": "PWO installation overview",
            "path": "wind-powered-car/step-6/installation_overview.png",
            "caption": "The PWO presents seven clickable installation stages.",
            "alt": "Wind Powered Car PWO installation overview with seven step buttons."
          },
          {
            "id": "pwo-step1",
            "label": "PWO Step 1 selected",
            "path": "wind-powered-car/step-6/installation_step_1.png",
            "caption": "The selected stage enlarges the frame and first hardware placement.",
            "alt": "Wind Powered Car PWO with installation Step 1 selected."
          },
          {
            "id": "physical-guide",
            "label": "Physical guide steps 3–6",
            "path": "wind-powered-car/step-6/physical_build_guide_steps_3_to_6.png",
            "caption": "The printed guide confirms battery-holder tape and orientation, wire routing, wheel spacing, propeller attachment, battery installation, and the switch.",
            "alt": "Printed build guide showing battery holder, wire routing, wheels, propeller, and switch."
          }
        ]
      },
      {
        "id": "assembly-checklist",
        "type": "build_checklist",
        "title": "Guided assembly record",
        "prompt": "Mark a stage complete only after comparing the real build with the supplied guide. Keep batteries out until the final test step.",
        "save_as": "assembly_checklist",
        "statuses": [
          {
            "id": "complete",
            "label": "Completed and checked"
          },
          {
            "id": "help",
            "label": "Need teacher help"
          },
          {
            "id": "not_yet",
            "label": "Not yet"
          }
        ],
        "items": [
          {
            "id": "frame_brackets",
            "label": "Attach the four axle angle brackets to the chassis as shown",
            "help": "The brackets must face the correct direction and remain secure."
          },
          {
            "id": "motor_bracket",
            "label": "Secure the motor within the motor bracket and attach it to the chassis",
            "help": "The motor shaft must point outward with room for the propeller."
          },
          {
            "id": "battery_holder",
            "label": "Attach the double-sided tape and mount the battery holder in the shown orientation",
            "help": "Keep the switch OFF and batteries out."
          },
          {
            "id": "wire_routing",
            "label": "Route the black and red wires as shown and connect them securely",
            "help": "Follow the guide’s left/right wire routing; do not leave bare wires touching."
          },
          {
            "id": "axles_wheels",
            "label": "Insert both axles, add all four wheels, and leave a small gap so wheels do not rub",
            "help": "The axles should be straight and parallel."
          },
          {
            "id": "propeller",
            "label": "Press the propeller onto the motor shaft with clear space around every blade",
            "help": "Do not power the motor while attaching the propeller."
          },
          {
            "id": "final_power_off_check",
            "label": "Confirm fasteners, wheel clearance, propeller clearance, wires, polarity, and switch OFF before batteries",
            "help": "A teacher should approve the first powered test."
          }
        ]
      },
      {
        "id": "wheel-check",
        "type": "build_checklist",
        "title": "Mechanical freedom check",
        "prompt": "With power disconnected, test each moving part by hand.",
        "save_as": "wheel_free_spin_check",
        "statuses": [
          {
            "id": "complete",
            "label": "Moves freely"
          },
          {
            "id": "help",
            "label": "Rubs or needs help"
          },
          {
            "id": "not_yet",
            "label": "Not checked"
          }
        ],
        "items": [
          {
            "id": "front_left",
            "label": "Front-left wheel spins freely"
          },
          {
            "id": "front_right",
            "label": "Front-right wheel spins freely"
          },
          {
            "id": "rear_left",
            "label": "Rear-left wheel spins freely"
          },
          {
            "id": "rear_right",
            "label": "Rear-right wheel spins freely"
          },
          {
            "id": "propeller_clear",
            "label": "Propeller can rotate without touching the frame, wires, or floor"
          }
        ]
      },
      {
        "id": "power-off-confirm",
        "type": "quiz_question",
        "title": "Safety confirmation",
        "prompt": "What must be true while you adjust a wheel, wire, motor, or propeller?",
        "choices": [
          {
            "id": "power_disconnected",
            "label": "Power is disconnected and the switch is OFF"
          },
          {
            "id": "motor_running",
            "label": "The motor is running so the problem is easier to see"
          },
          {
            "id": "hold_propeller",
            "label": "Hold the propeller while power stays on"
          },
          {
            "id": "add_water",
            "label": "Add water to reduce rubbing"
          }
        ],
        "correct_answer": "power_disconnected",
        "save_as": "power_off_build_confirmation",
        "correct_feedback": "Correct. Observe first, disconnect power, then adjust.",
        "incorrect_feedback": "Never touch or adjust moving/electrical parts while powered."
      },
      {
        "id": "assembly-note",
        "type": "evidence_response",
        "title": "Record one build challenge or successful check",
        "prompt": "What problem did you correct, or what evidence showed that a stage was assembled correctly?",
        "placeholder": "At first... We checked... Then...",
        "save_as": "assembly_problem_note",
        "min_length": 20,
        "required": true
      },
      {
        "id": "step6-saras",
        "type": "saras_checkpoint",
        "title": "Troubleshoot the build with Saras",
        "suggested_questions": [
          "Which PWO stage should I revisit for this part?",
          "How can I check wheel clearance with power off?",
          "What evidence shows an axle is crooked?",
          "Help me describe the problem without guessing the fix."
        ],
        "hidden_context": "The student is assembling the physical Wind Powered Car. Use assembly_checklist, wheel_free_spin_check, power_off_build_confirmation, and assembly_problem_note. The supplied physical guide has six major steps: attach four angle brackets; secure motor in bracket; tape and mount battery holder; route red/black wires; install axles/wheels with a gap; attach propeller, then insert batteries and use the switch only for the approved test. The PWO has seven clickable stages. Prioritize the supplied guide and teacher confirmation. Never invent missing hardware or encourage powered adjustments. Ask the learner to identify the current stage and observed mismatch.",
        "reveal_policy": "Assembly guidance may be given only from the supplied physical/PWO instructions. Safety overrides speed."
      },
      {
        "id": "apply-completion",
        "type": "completion_gate",
        "incomplete_title": "Complete the safe rover build",
        "complete_title": "Apply complete: rover built and mechanically checked",
        "incomplete_prompt": "Verify the kit and safety rules, complete the guided assembly, check mechanical freedom, and record build evidence.",
        "complete_prompt": "The rover is assembled and checked. Next, Test & Improve it using controlled evidence.",
        "rules": [
          {
            "field": "parts_readiness_check",
            "condition": "min_items",
            "value": 9
          },
          {
            "field": "safety_readiness_check",
            "condition": "all_equal",
            "value": "complete"
          },
          {
            "field": "failure_cause_prediction",
            "condition": "answered"
          },
          {
            "field": "readiness_prevention_plan",
            "condition": "min_length",
            "value": 20
          },
          {
            "field": "installation_pwo_opened",
            "condition": "truthy"
          },
          {
            "field": "assembly_checklist",
            "condition": "all_equal",
            "value": "complete"
          },
          {
            "field": "wheel_free_spin_check",
            "condition": "all_equal",
            "value": "complete"
          },
          {
            "field": "power_off_build_confirmation",
            "condition": "equals",
            "value": "power_disconnected"
          },
          {
            "field": "assembly_problem_note",
            "condition": "min_length",
            "value": 20
          }
        ]
      }
    ]
  },
  "wind-car-test-improve": {
    "step_id": "wind-car-test-improve",
    "title": "Test, Measure, and Improve",
    "subtitle": "From First Spin to Version 2",
    "phase": "Test & Improve",
    "story": "Wake up the circuit safely, measure the first rescue run before changing anything, then redesign one evidence-based variable and compare Version 2 with Version 1 under fair conditions.",
    "self_guided": {
      "title": "Test the rover and improve fairly",
      "say": "Different tests are useful only if you record what changed. Keep hands, clothing, and loose objects away from the spinning propeller.",
      "do_now": "With a clear test lane and the safety checks complete, record the first motor and travel tests, pick one redesign variable, predict its effect, and compare the evidence from two trials.",
      "help_prompt": "Professor Swan, guide me through this phase one action at a time, using the mission evidence and without giving me the answers or skipping safety checks.",
      "transition_summary": "You recorded a first spin and travel trial, investigated friction or circuit issues, and compared a second run after one planned change.",
      "next_preview": "Next, use those trial records to explain how the rover works and whether it meets the rescue goal."
    },
    "self_guided_block_guidance": {
      "trial1-prediction": {
        "title": "Commit your first-run prediction",
        "try_now": "Before running the rover, decide what you will measure."
      },
      "redesign-variable": {
        "title": "Choose only one redesign",
        "try_now": "Use your first measurements and keep the lane and test conditions comparable."
      }
    },
    "blocks": [
      {
        "id": "step7-story",
        "type": "story_hook",
        "eyebrow": "FIRST POWER TEST",
        "title": "Observe first; adjust only after power is OFF",
        "body": "Check four questions in order: Does the motor run? Does the propeller spin? Which way does the air move? Do the wheels still roll freely after power is switched off?",
        "tone": "mystery"
      },
      {
        "id": "diagnostic-image",
        "type": "image_gallery",
        "title": "Use the safe diagnostic sequence",
        "prompt": "Change one thing at a time so each test produces useful evidence.",
        "save_as": "diagnostic_image_examined",
        "images": [
          {
            "id": "diagnostic",
            "label": "First-spin diagnostic",
            "path": "wind-powered-car/step-7/first_spin_diagnostic.png",
            "caption": "Power briefly, observe airflow, switch off, check wheels, then select one next action.",
            "alt": "Four-step safe diagnostic for the first powered propeller test."
          }
        ]
      },
      {
        "id": "build-summary",
        "type": "prior_work_summary",
        "title": "Review the assembly evidence",
        "prompt": "Use the build record rather than starting from memory.",
        "items": [
          {
            "source_step_id": "wind-car-apply",
            "field": "assembly_problem_note",
            "label": "Build challenge or successful check"
          },
          {
            "source_step_id": "wind-car-apply",
            "field": "power_off_build_confirmation",
            "label": "Power-off safety confirmation",
            "value_labels": {
              "power_disconnected": "Power was disconnected during adjustments"
            }
          }
        ]
      },
      {
        "id": "first-spin-record",
        "master_teacher_response_kind": "reported_trial_result",
        "master_teacher_briefing": {"title": "Before the first powered propeller test", "steps": ["With power OFF, check the propeller is secure and the lane is clear.", "Keep hands, hair and clothes far from the propeller. Make one short powered test.", "Switch OFF before touching anything. Record what the motor, propeller and wheels actually did."], "spoken": "Ready for your first rover test? Power OFF while checking the propeller and clear lane. Keep fingers, hair and clothes away from the propeller. Make one brief test, then switch OFF before touching the rover. Record what you really observed."},
        "type": "experiment_record",
        "title": "Record the first powered test",
        "prompt": "Use only a brief, teacher-approved test. Switch OFF before any adjustment.",
        "save_as": "first_spin_record",
        "fields": [
          {
            "id": "motor_result",
            "label": "Did the motor run?",
            "type": "choice",
            "choices": [
              {
                "id": "yes",
                "label": "Yes"
              },
              {
                "id": "no",
                "label": "No"
              },
              {
                "id": "intermittent",
                "label": "Started and stopped"
              }
            ]
          },
          {
            "id": "propeller_result",
            "label": "Did the propeller spin freely?",
            "type": "choice",
            "choices": [
              {
                "id": "free",
                "label": "Yes, freely"
              },
              {
                "id": "rubbing",
                "label": "It rubbed or struck something"
              },
              {
                "id": "not_spin",
                "label": "It did not spin"
              }
            ]
          },
          {
            "id": "air_direction",
            "label": "Where did you feel or observe the airflow?",
            "type": "choice",
            "choices": [
              {
                "id": "behind",
                "label": "Behind the propeller / backward"
              },
              {
                "id": "forward",
                "label": "Toward the rover’s front"
              },
              {
                "id": "unclear",
                "label": "Unclear—need a paper-strip test"
              }
            ]
          },
          {
            "id": "sound_motion",
            "label": "What did you hear or notice?",
            "type": "text",
            "placeholder": "The motor sounded... The propeller...",
            "min_length": 10
          }
        ]
      },
      {
        "id": "open-circuit-diagnostic",
        "type": "quiz_question",
        "title": "Circuit diagnostic",
        "prompt": "The motor does not run. What is the safest first action?",
        "choices": [
          {
            "id": "switch_off_trace",
            "label": "Switch OFF, then trace the battery, switch, wires, and motor connections"
          },
          {
            "id": "hold_propeller",
            "label": "Hold the propeller and keep power on"
          },
          {
            "id": "add_battery",
            "label": "Add extra batteries not supplied with the kit"
          },
          {
            "id": "bend_wire_powered",
            "label": "Bend wires while the motor remains powered"
          }
        ],
        "correct_answer": "switch_off_trace",
        "save_as": "diagnostic_action_check",
        "correct_feedback": "Correct. Power off first, then trace one complete path and check one connection at a time.",
        "incorrect_feedback": "Safety first: disconnect power before touching the circuit or propeller."
      },
      {
        "id": "rover-master-teacher-diagnostic",
        "type": "master_teacher_diagnostic",
        "title": "Debug the rover with Professor Swan",
        "prompt": "Your first result is evidence. Select a symptom and investigate one clue at a time.",
        "save_as": "rover_diagnostic_state",
        "kit": "Green Valley Rover",
        "safety": "Power OFF before all checks. Keep fingers, hair and loose clothes clear of the propeller. Get adult help for damaged wires, hot parts or unsafe tools.",
        "cases": [
          {
            "id": "silent",
            "label": "The motor or propeller does not turn",
            "checks": [
              {
                "id": "power",
                "instruction": "With power OFF, does the switch appear to be in the intended position according to the kit guide?"
              },
              {
                "id": "prop",
                "instruction": "With batteries removed, is the propeller clear of the chassis and other objects? Do not force it."
              },
              {
                "id": "damage",
                "instruction": "Are there visible loose or damaged wires? Do not touch damaged wiring; ask an adult if anything looks unsafe."
              }
            ]
          },
          {
            "id": "spins_still",
            "label": "Propeller spins, but rover barely moves",
            "checks": [
              {
                "id": "air",
                "instruction": "During the test, which way did the air seem to move, and did the rover respond the opposite way?"
              },
              {
                "id": "wheels",
                "instruction": "With power OFF, can the wheels turn freely without rubbing the frame?"
              },
              {
                "id": "lane",
                "instruction": "Is the test lane flat and clear, with a lightweight cargo load?"
              }
            ]
          },
          {
            "id": "veers",
            "label": "The rover veers or struggles to roll straight",
            "checks": [
              {
                "id": "wheel",
                "instruction": "With power OFF, are both wheels seated evenly and free of rubbing?"
              },
              {
                "id": "surface",
                "instruction": "Does the test lane look level, clear and similar on both sides?"
              }
            ]
          },
          {
            "id": "unsafe",
            "label": "Something looks unsafe",
            "checks": [
              {
                "id": "stop",
                "instruction": "Keep power OFF, avoid the propeller and ask an adult to check any damaged or hot component."
              }
            ]
          }
        ]
      },
      {
        "id": "diagnostic-note",
        "type": "evidence_response",
        "title": "Choose the next diagnostic action",
        "prompt": "Based on your evidence, what single check or correction should happen next—and why?",
        "placeholder": "Our evidence shows... We will switch off and check...",
        "save_as": "diagnostic_action",
        "min_length": 25,
        "required": true
      },
      {
        "id": "step7-saras",
        "type": "saras_checkpoint",
        "title": "Diagnose with Saras one check at a time",
        "suggested_questions": [
          "The motor is silent. What should I check first?",
          "The propeller spins but airflow seems forward. What evidence should I collect?",
          "How can I separate a circuit problem from wheel rubbing?",
          "Can you help me choose one safe next check?"
        ],
        "hidden_context": "The student is performing the first brief powered diagnostic. Use first_spin_record, diagnostic_action_check, diagnostic_action, and prior assembly evidence. Give one safe diagnostic check at a time. Distinguish open-circuit/connection problems, blocked propeller, reversed motor direction, and mechanical wheel rubbing. Require power OFF before touching any part. Do not assume a part is broken without evidence.",
        "reveal_policy": "Full circuit, motor, propeller, airflow, and wheel diagnostics may now be discussed. Keep the task to one variable/check at a time."
      },
      {
        "id": "step8-story",
        "type": "story_hook",
        "eyebrow": "TRIAL 1",
        "title": "A result is useful only when the test conditions are recorded",
        "body": "Use the same start line, surface, lightweight payload, and test method you plan to repeat in Version 2. Record distance, time, path, wheel behavior, and whether the medicine stayed aboard.",
        "tone": "mystery"
      },
      {
        "id": "lane-image",
        "type": "image_gallery",
        "title": "Prepare the rescue lane",
        "prompt": "Use a teacher-marked lane. Record its chosen length rather than assuming every room has the same space.",
        "save_as": "rescue_lane_examined",
        "images": [
          {
            "id": "lane",
            "label": "Measurement lane",
            "path": "wind-powered-car/step-8/rescue_test_lane.png",
            "caption": "Use the same start line, surface, payload, and test time for both trials.",
            "alt": "Straight floor test lane with start line and measurement marks."
          }
        ]
      },
      {
        "id": "trial-prior-work",
        "type": "prior_work_summary",
        "title": "Bring forward your predictions and diagnostic",
        "prompt": "The first run should test an idea you already recorded.",
        "items": [
          {
            "source_step_id": "wind-car-wonder",
            "field": "initial_motion_theory",
            "label": "Opening motion theory"
          },
          {
            "source_step_id": "wind-car-test-improve",
            "field": "diagnostic_action",
            "label": "Last diagnostic decision"
          }
        ]
      },
      {
        "id": "trial1-prediction",
        "type": "choice_cards",
        "title": "Predict the first run",
        "prompt": "Choose the result you expect most strongly.",
        "choices": [
          {
            "id": "straight_far",
            "label": "Travels mostly straight and reaches the target"
          },
          {
            "id": "short_distance",
            "label": "Moves, but stops before the target"
          },
          {
            "id": "turns",
            "label": "Turns left or right instead of staying straight"
          },
          {
            "id": "wheels_rub",
            "label": "Moves poorly because wheels rub or wobble"
          },
          {
            "id": "no_motion",
            "label": "Propeller spins but the rover barely moves"
          }
        ],
        "save_as": "trial_one_prediction",
        "required": true
      },
      {
        "id": "trial1-record",
        "master_teacher_response_kind": "reported_trial_result",
        "master_teacher_briefing": {"title": "Rover Trial 1 — collect real evidence", "steps": ["Choose a clear, flat lane and predict the rover movement.", "Keep everyone clear of the spinning propeller. Make one short test.", "Turn power OFF, then record distance and what you observed."], "spoken": "Rover Trial One. Choose a clear flat lane and make a prediction. Keep away from the spinning propeller during a short test. Turn power OFF, then record the actual movement and any problems."},
        "type": "experiment_record",
        "title": "Trial 1 evidence record",
        "prompt": "Record the conditions and result before changing anything.",
        "save_as": "trial_one_record",
        "fields": [
          {
            "id": "lane_length",
            "label": "Chosen lane length or target distance",
            "type": "text",
            "placeholder": "Example: 1 meter or teacher-marked finish line",
            "min_length": 2
          },
          {
            "id": "surface",
            "label": "Test surface",
            "type": "choice",
            "choices": [
              {
                "id": "smooth_floor",
                "label": "Smooth floor"
              },
              {
                "id": "paper",
                "label": "Large paper sheet"
              },
              {
                "id": "low_carpet",
                "label": "Low carpet"
              },
              {
                "id": "other",
                "label": "Other recorded surface"
              }
            ]
          },
          {
            "id": "payload",
            "label": "Lightweight rescue payload",
            "type": "text",
            "placeholder": "What did the rover carry?",
            "min_length": 2
          },
          {
            "id": "distance",
            "label": "Distance traveled",
            "type": "text",
            "placeholder": "Record a number and unit or finish-line result",
            "min_length": 2
          },
          {
            "id": "time",
            "label": "Travel time or test duration",
            "type": "text",
            "placeholder": "Record seconds or the agreed run time",
            "min_length": 1
          },
          {
            "id": "path",
            "label": "Path",
            "type": "choice",
            "choices": [
              {
                "id": "straight",
                "label": "Mostly straight"
              },
              {
                "id": "left",
                "label": "Turned left"
              },
              {
                "id": "right",
                "label": "Turned right"
              },
              {
                "id": "wobbled",
                "label": "Wobbled or wandered"
              }
            ]
          },
          {
            "id": "payload_result",
            "label": "Payload result",
            "type": "choice",
            "choices": [
              {
                "id": "delivered",
                "label": "Stayed aboard / delivered"
              },
              {
                "id": "fell",
                "label": "Fell off"
              },
              {
                "id": "not_used",
                "label": "No payload this trial"
              }
            ]
          }
        ]
      },
      {
        "id": "friction-video-block",
        "type": "video_asset",
        "title": "Optional: The Friction Trap",
        "mission": "Open only after Trial 1. Compare the video’s wheel and axle clues with your own evidence.",
        "asset": {
          "id": "friction-video",
          "type": "saras_video",
          "asset_ref": {
            "source_video_id": 7049,
            "tag_id": 30066
          },
          "provenance": {
            "source_type": "collection",
            "collection_id": 168,
            "collection_item_id": 143
          },
          "content_context": {
            "base_board": "saras",
            "language_id": 1,
            "board_topic_id": 934,
            "source_topic_id": 960
          }
        }
      },
      {
        "id": "friction-check",
        "type": "quiz_question",
        "title": "Evidence check: surface and rubbing",
        "prompt": "Which change would usually make the same rover harder to move?",
        "choices": [
          {
            "id": "more_friction",
            "label": "A rougher surface or wheels rubbing the frame"
          },
          {
            "id": "free_wheels",
            "label": "Straighter axles and freely spinning wheels"
          },
          {
            "id": "clear_propeller",
            "label": "A propeller with clear space"
          },
          {
            "id": "secure_wire",
            "label": "A secure electrical connection"
          }
        ],
        "correct_answer": "more_friction",
        "save_as": "friction_checkpoint",
        "correct_feedback": "Correct. More rubbing means more of the air-push is lost overcoming friction.",
        "incorrect_feedback": "Look for the choice that increases rubbing or resistance to rolling."
      },
      {
        "id": "trial1-observation",
        "type": "evidence_response",
        "title": "Describe what the rover actually did",
        "prompt": "Use at least two observations: motion, path, wheels, propeller, sound, or payload.",
        "placeholder": "The rover... I also observed...",
        "save_as": "trial_one_observation",
        "min_length": 25,
        "required": true
      },
      {
        "id": "first-run-problem",
        "type": "evidence_response",
        "title": "Identify the strongest improvement opportunity",
        "prompt": "What single problem or goal should Version 2 address? Cite evidence from Trial 1.",
        "placeholder": "The strongest evidence is... Therefore Version 2 should...",
        "save_as": "first_run_problem",
        "min_length": 30,
        "required": true
      },
      {
        "id": "step8-saras",
        "type": "saras_checkpoint",
        "title": "Analyze Trial 1 with Saras",
        "suggested_questions": [
          "Which observation is strongest evidence of friction?",
          "Why might the rover turn even if the propeller spins?",
          "How can I separate a wheel problem from a thrust problem?",
          "Help me choose one Version 2 goal from my data."
        ],
        "hidden_context": "The student has completed Trial 1 of the Wind Powered Car. Use trial_one_prediction, trial_one_record, friction_checkpoint, trial_one_observation, and first_run_problem, plus prior diagnostic evidence. Ask what evidence shows energy loss or uneven motion. Possible causes include wheel rubbing, crooked axles, wire drag, propeller clearance/direction, weak connection, or payload placement. Do not prescribe a change until the student identifies evidence.",
        "reveal_policy": "Friction, alignment, thrust, and fair-test reasoning may be discussed. Do not choose the redesign variable for the student."
      },
      {
        "id": "step9-story",
        "type": "story_hook",
        "eyebrow": "REDESIGN WINDOW",
        "title": "One change. Same test. New evidence.",
        "body": "Changing many things at once may improve the rover, but you will not know which change mattered. A fair comparison isolates one variable.",
        "tone": "mystery"
      },
      {
        "id": "fair-test-image",
        "type": "image_gallery",
        "title": "Plan the fair comparison",
        "prompt": "Use Trial 1 as the baseline and keep all major conditions the same.",
        "save_as": "fair_test_image_examined",
        "images": [
          {
            "id": "fair-test",
            "label": "Trial 1 versus Trial 2",
            "path": "wind-powered-car/step-9/fair_test_version_two.png",
            "caption": "Choose one variable, repeat the same test, and compare evidence.",
            "alt": "Fair-test diagram comparing Trial 1 and Trial 2."
          }
        ]
      },
      {
        "id": "trial1-summary",
        "type": "prior_work_summary",
        "title": "Review the baseline",
        "prompt": "Version 2 must respond to the evidence, not a random preference.",
        "items": [
          {
            "source_step_id": "wind-car-test-improve",
            "field": "trial_one_record",
            "label": "Trial 1 record",
            "field_labels": {
              "lane_length": "Lane",
              "surface": "Surface",
              "payload": "Payload",
              "distance": "Distance",
              "time": "Time",
              "path": "Path",
              "payload_result": "Payload"
            },
            "value_labels": {
              "smooth_floor": "Smooth floor",
              "paper": "Paper",
              "low_carpet": "Low carpet",
              "straight": "Mostly straight",
              "left": "Turned left",
              "right": "Turned right",
              "wobbled": "Wobbled",
              "delivered": "Delivered",
              "fell": "Fell off",
              "not_used": "No payload"
            }
          },
          {
            "source_step_id": "wind-car-test-improve",
            "field": "first_run_problem",
            "label": "Chosen improvement opportunity"
          }
        ]
      },
      {
        "id": "redesign-variable",
        "master_teacher_response_kind": "planned_change",
        "type": "choice_cards",
        "title": "Choose ONE redesign variable",
        "prompt": "Choose the change best supported by your Trial 1 evidence.",
        "choices": [
          {
            "id": "wheel_clearance",
            "label": "Increase wheel clearance so wheels do not rub"
          },
          {
            "id": "axle_alignment",
            "label": "Straighten and align the axles"
          },
          {
            "id": "wire_routing",
            "label": "Secure wires so they do not drag or touch moving parts"
          },
          {
            "id": "propeller_clearance",
            "label": "Improve propeller clearance or confirm correct orientation"
          },
          {
            "id": "connection_security",
            "label": "Secure one loose electrical connection"
          },
          {
            "id": "payload_position",
            "label": "Move the same payload to a more stable position"
          },
          {
            "id": "diagnose_first",
            "label": "Do not redesign yet—collect one more diagnostic observation"
          }
        ],
        "save_as": "selected_redesign_variable",
        "required": true
      },
      {
        "id": "redesign-prediction",
        "type": "evidence_response",
        "title": "Predict the effect",
        "prompt": "What should improve, and why? Name the evidence from Trial 1 that supports your change.",
        "placeholder": "Because Trial 1 showed..., changing... should...",
        "save_as": "redesign_prediction",
        "min_length": 35,
        "required": true
      },
      {
        "id": "taming-thrust-video-block",
        "type": "video_asset",
        "title": "Optional: Taming the Thrust",
        "mission": "Open after choosing a variable. Use only the ideas that match your actual kit and test evidence.",
        "asset": {
          "id": "taming-thrust-video",
          "type": "saras_video",
          "asset_ref": {
            "source_video_id": 7048,
            "tag_id": 30065
          },
          "provenance": {
            "source_type": "collection",
            "collection_id": 168,
            "collection_item_id": 143
          },
          "content_context": {
            "base_board": "saras",
            "language_id": 1,
            "board_topic_id": 934,
            "source_topic_id": 960
          }
        }
      },
      {
        "id": "trial2-record",
        "master_teacher_response_kind": "reported_trial_result",
        "master_teacher_briefing": {"title": "Rover Trial 2 — change one thing", "steps": ["Switch OFF before making your ONE chosen adjustment.", "Keep the lane, distance and cargo conditions comparable.", "Try again safely, turn OFF and compare the two observations."], "spoken": "For Trial Two, change just one thing with power OFF. Keep the lane and cargo conditions similar. Try again briefly, stop, and compare your evidence. What changed?"},
        "type": "experiment_record",
        "title": "Trial 2 evidence record",
        "prompt": "Use the same lane, surface, payload, and test method as Trial 1.",
        "save_as": "trial_two_record",
        "fields": [
          {
            "id": "same_conditions",
            "label": "Were the main test conditions kept the same?",
            "type": "choice",
            "choices": [
              {
                "id": "yes",
                "label": "Yes"
              },
              {
                "id": "mostly",
                "label": "Mostly; one unavoidable difference recorded"
              },
              {
                "id": "no",
                "label": "No—comparison is limited"
              }
            ]
          },
          {
            "id": "change_made",
            "label": "Exact one change made",
            "type": "text",
            "placeholder": "We changed...",
            "min_length": 5
          },
          {
            "id": "distance",
            "label": "Distance traveled",
            "type": "text",
            "placeholder": "Record the same unit or finish-line result",
            "min_length": 2
          },
          {
            "id": "time",
            "label": "Travel time or test duration",
            "type": "text",
            "placeholder": "Record using the same method as Trial 1",
            "min_length": 1
          },
          {
            "id": "path",
            "label": "Path",
            "type": "choice",
            "choices": [
              {
                "id": "straight",
                "label": "Mostly straight"
              },
              {
                "id": "left",
                "label": "Turned left"
              },
              {
                "id": "right",
                "label": "Turned right"
              },
              {
                "id": "wobbled",
                "label": "Wobbled or wandered"
              }
            ]
          },
          {
            "id": "payload_result",
            "label": "Payload result",
            "type": "choice",
            "choices": [
              {
                "id": "delivered",
                "label": "Stayed aboard / delivered"
              },
              {
                "id": "fell",
                "label": "Fell off"
              },
              {
                "id": "not_used",
                "label": "No payload"
              }
            ]
          }
        ]
      },
      {
        "id": "comparison-claim",
        "type": "evidence_response",
        "title": "Compare Trial 1 and Trial 2",
        "prompt": "Make a claim about improvement, no change, or a new tradeoff. Use measurements or observations from both trials.",
        "placeholder": "Trial 1... Trial 2... This evidence shows...",
        "save_as": "trial_comparison",
        "min_length": 45,
        "required": true
      },
      {
        "id": "redesign-conclusion",
        "type": "choice_cards",
        "title": "What is your engineering conclusion?",
        "prompt": "Unexpected results are still useful.",
        "choices": [
          {
            "id": "improved",
            "label": "The selected goal improved"
          },
          {
            "id": "mixed",
            "label": "One result improved but another became worse"
          },
          {
            "id": "no_clear_change",
            "label": "There is no clear improvement yet"
          },
          {
            "id": "new_problem",
            "label": "The change revealed a different problem"
          },
          {
            "id": "unfair_test",
            "label": "The test conditions changed too much to conclude"
          }
        ],
        "save_as": "redesign_conclusion",
        "required": true
      },
      {
        "id": "step9-saras",
        "type": "saras_checkpoint",
        "title": "Defend the redesign with Saras",
        "suggested_questions": [
          "Was my comparison fair enough to support a claim?",
          "What stayed the same between trials?",
          "Which measurement best supports my conclusion?",
          "Did my change create a tradeoff?"
        ],
        "hidden_context": "The student is comparing Wind Powered Car Trial 1 and Trial 2. Use selected_redesign_variable, redesign_prediction, trial_two_record, trial_comparison, redesign_conclusion, and prior Trial 1 evidence. Challenge fair-test quality. Ask what stayed the same, what changed, and which measurement or observation supports the claim. Accept no improvement or a tradeoff as valid engineering evidence. Do not rewrite the conclusion for the student.",
        "reveal_policy": "All relevant rover science may be discussed. Preserve student ownership of the claim and evidence."
      },
      {
        "id": "test-improve-completion",
        "type": "completion_gate",
        "incomplete_title": "Complete the test-and-improve cycle",
        "complete_title": "Test & Improve complete: Version 2 evidence recorded",
        "incomplete_prompt": "Run the safe diagnostic, record Version 1, choose one evidence-based change, run Version 2 under comparable conditions, and defend the comparison.",
        "complete_prompt": "You completed an evidence-based redesign. Next, Explain how the rover works and what the trials show.",
        "rules": [
          {
            "field": "first_spin_record",
            "condition": "record_complete"
          },
          {
            "field": "diagnostic_action_check",
            "condition": "equals",
            "value": "switch_off_trace"
          },
          {
            "field": "diagnostic_action",
            "condition": "min_length",
            "value": 25
          },
          {
            "field": "trial_one_prediction",
            "condition": "answered"
          },
          {
            "field": "trial_one_record",
            "condition": "record_complete"
          },
          {
            "field": "friction_checkpoint",
            "condition": "equals",
            "value": "more_friction"
          },
          {
            "field": "trial_one_observation",
            "condition": "min_length",
            "value": 25
          },
          {
            "field": "first_run_problem",
            "condition": "min_length",
            "value": 30
          },
          {
            "field": "selected_redesign_variable",
            "condition": "answered"
          },
          {
            "field": "redesign_prediction",
            "condition": "min_length",
            "value": 35
          },
          {
            "field": "trial_two_record",
            "condition": "record_complete"
          },
          {
            "field": "trial_comparison",
            "condition": "min_length",
            "value": 45
          },
          {
            "field": "redesign_conclusion",
            "condition": "answered"
          }
        ]
      }
    ]
  },
  "wind-car-explain": {
    "step_id": "wind-car-explain",
    "title": "Deliver the Supplies!",
    "subtitle": "Engineer’s Rescue Report",
    "phase": "Explain",
    "story": "The rescue is complete only when you can explain the rover’s mechanism honestly and defend your engineering decision with evidence from both trials.",
    "self_guided": {
      "title": "Deliver the evidence, not just the package",
      "say": "Engineers must show why their solution works and whether it meets the rescue rules. A failed trial also belongs in the report.",
      "do_now": "Build the final power-and-motion chain, then use your actual trial data to write the Rescue Engineer report.",
      "help_prompt": "Professor Swan, guide me through this phase one action at a time, using the mission evidence and without giving me the answers or skipping safety checks.",
      "transition_summary": "You reconstructed the battery-to-airflow energy chain and wrote a rescue report using evidence from your rover trials.",
      "next_preview": "Next, compare this explanation with the prediction you made before inspecting the rover."
    },
    "self_guided_block_guidance": {
      "engineer-report": {
        "title": "Make the rescue case",
        "try_now": "Use measured trial evidence and acknowledge any mission constraint you did not meet."
      }
    },
    "blocks": [
      {
        "id": "step10-story",
        "type": "story_hook",
        "eyebrow": "FINAL MISSION REPORT",
        "title": "A successful engineer explains cause and effect with evidence",
        "body": "Your report must connect battery, circuit, motor, propeller, moving air, thrust, wheels, friction, and redesign evidence. Do not call the battery kit a natural-wind vehicle.",
        "tone": "mystery"
      },
      {
        "id": "final-chain-image",
        "type": "image_gallery",
        "title": "Use the final system model",
        "prompt": "The model shows the battery-powered kit. The real-wind gallery is optional enrichment and represents a different energy source.",
        "save_as": "final_image_examined",
        "images": [
          {
            "id": "final-chain",
            "label": "Battery-powered rover chain",
            "path": "wind-powered-car/step-10/final_energy_chain.png",
            "caption": "Battery → closed circuit → motor → propeller → air backward → rover forward.",
            "alt": "Final energy and motion chain for the battery-powered rover."
          },
          {
            "id": "real-wind",
            "label": "Actual outdoor-wind systems",
            "path": "wind-powered-car/step-10/real_wind_examples.jpg",
            "caption": "A wind turbine, sailboat, and pinwheel use environmental wind. They are different from this battery-powered propeller rover.",
            "alt": "Wind turbine, sailboat, and pinwheel as examples of outdoor wind systems."
          },
          {
            "id": "completed-pwo",
            "label": "Completed PWO rover",
            "path": "wind-powered-car/step-10/completed_rover_pwo.png",
            "caption": "Can you trace the chain? Batteries supply electricity to the motor; the propeller pushes air backward and the rover rolls forward. This rover does not need outdoor wind.",
            "alt": "Completed Wind Powered Car PWO with the assembled rover and learning summary."
          }
        ]
      },
      {
        "id": "final-prior-work",
        "type": "prior_work_summary",
        "title": "Bring the evidence into the report",
        "prompt": "Use recorded work from the beginning and both trials.",
        "items": [
          {
            "source_step_id": "wind-car-wonder",
            "field": "initial_motion_theory",
            "label": "Opening theory"
          },
          {
            "source_step_id": "wind-car-test-improve",
            "field": "trial_one_observation",
            "label": "Trial 1 observation"
          },
          {
            "source_step_id": "wind-car-test-improve",
            "field": "trial_comparison",
            "label": "Trial comparison"
          },
          {
            "source_step_id": "wind-car-test-improve",
            "field": "redesign_conclusion",
            "label": "Version 2 conclusion",
            "value_labels": {
              "improved": "Selected goal improved",
              "mixed": "Improvement with a tradeoff",
              "no_clear_change": "No clear improvement",
              "new_problem": "A new problem appeared",
              "unfair_test": "Comparison was not fair enough"
            }
          }
        ]
      },
      {
        "id": "final-energy-chain",
        "type": "sequence_builder",
        "title": "Rebuild the complete cause-and-effect chain",
        "prompt": "Arrange the events from stored energy to rolling rescue motion.",
        "save_as": "final_energy_chain",
        "items": [
          {
            "id": "chemical",
            "label": "Chemical energy is stored in the batteries"
          },
          {
            "id": "electrical",
            "label": "A closed circuit transfers electrical energy"
          },
          {
            "id": "rotation",
            "label": "The motor converts electrical energy into rotation"
          },
          {
            "id": "air_backward",
            "label": "The propeller pushes air backward"
          },
          {
            "id": "thrust_forward",
            "label": "The air pushes the propeller and rover forward"
          },
          {
            "id": "roll",
            "label": "Free wheels and aligned axles let the rover roll"
          }
        ],
        "correct_order": [
          "chemical",
          "electrical",
          "rotation",
          "air_backward",
          "thrust_forward",
          "roll"
        ],
        "correct_feedback": "Correct. Your chain explains both energy changes and the direction of the push.",
        "retry_feedback": "Start with the stored energy. Then ask what must happen before air moves and before the rover rolls.",
        "required": true
      },
      {
        "id": "engineer-report",
        "type": "experiment_record",
        "title": "Prepare the Engineer’s Rescue Report",
        "prompt": "Write in your own voice. Saras may ask questions, but should not write the report for you.",
        "save_as": "engineers_rescue_report",
        "fields": [
          {
            "id": "problem",
            "label": "1. What rescue problem and constraints did your team face?",
            "type": "text",
            "placeholder": "The rangers needed... Our rover had to...",
            "min_length": 20
          },
          {
            "id": "mechanism",
            "label": "2. How does the battery-powered air-push rover work?",
            "type": "text",
            "placeholder": "The batteries... circuit... motor... propeller... air... wheels...",
            "min_length": 45
          },
          {
            "id": "trial_evidence",
            "label": "3. What evidence from Trial 1 and Trial 2 supports your explanation or redesign claim?",
            "type": "text",
            "placeholder": "In Trial 1... After changing... Trial 2...",
            "min_length": 45
          },
          {
            "id": "improvement",
            "label": "4. What did Version 2 improve, fail to improve, or trade off?",
            "type": "text",
            "placeholder": "Our conclusion is... because...",
            "min_length": 30
          },
          {
            "id": "version3",
            "label": "5. What would you investigate in Version 3?",
            "type": "text",
            "placeholder": "Next I would test... while keeping... the same.",
            "min_length": 20
          }
        ]
      },
      {
        "id": "step10-saras",
        "type": "saras_checkpoint",
        "title": "Rehearse the rescue report with Saras",
        "suggested_questions": [
          "Ask me one report question at a time.",
          "Does my mechanism explanation include the full causal chain?",
          "Which trial evidence best supports my redesign claim?",
          "Help me distinguish this kit from a real wind turbine without rewriting my answer."
        ],
        "hidden_context": "The student is preparing the final Wind Powered Car STEM Mission Engineer’s Rescue Report. Use final_energy_chain, engineers_rescue_report, and prior trial evidence. The accurate mechanism is batteries/closed circuit/motor/propeller/air backward/thrust forward/passive wheels. Ask one presentation question at a time, request evidence for claims, and preserve the student’s wording. Unexpected or negative redesign results are valid if supported. The separate Reflect and Grow phases follow this report.",
        "reveal_policy": "The complete rover science and all prior test evidence may be discussed. Coach, do not author the final report."
      },
      {
        "id": "explain-completion",
        "type": "completion_gate",
        "incomplete_title": "Finish the Engineer’s Rescue Report",
        "complete_title": "Explain complete: engineering claim defended",
        "incomplete_prompt": "Complete the final cause-and-effect chain and the five-part Engineer’s Rescue Report in your own words.",
        "complete_prompt": "You explained the rover and defended the redesign with evidence. Next, Reflect on how your thinking changed.",
        "rules": [
          {
            "field": "final_energy_chain",
            "condition": "sequence_correct"
          },
          {
            "field": "engineers_rescue_report",
            "condition": "record_complete"
          }
        ]
      }
    ]
  },
  "wind-car-reflect": {
    "step_id": "wind-car-reflect",
    "title": "What Changed Your Thinking?",
    "subtitle": "Reflect on Evidence, Failure, and Improvement",
    "phase": "Reflect",
    "story": "Return to your opening theory and compare it with the evidence you collected while inspecting, building, testing, and redesigning the rover.",
    "self_guided": {
      "title": "Look back at the investigation",
      "say": "Compare your first motion idea to what you learned from the motor, air, wheels, and tests.",
      "do_now": "Describe what changed in your reasoning and which observation was most convincing.",
      "help_prompt": "Professor Swan, guide me through this phase one action at a time, using the mission evidence and without giving me the answers or skipping safety checks.",
      "transition_summary": "You wrote which evidence supported or changed your original theory about the rover’s motion.",
      "next_preview": "Next, consider a new challenge and decide which engineering rule transfers to it."
    },
    "self_guided_block_guidance": {
      "final-reflection": {
        "title": "Revisit your first motion theory",
        "try_now": "What evidence helped you connect the motor, propeller, air, and rolling wheels?"
      }
    },
    "blocks": [
      {
        "id": "reflect-story",
        "type": "story_hook",
        "eyebrow": "REFLECT",
        "title": "Look back at the evidence that changed your thinking",
        "body": "Engineers do more than report a result. Compare your opening theory with what the rover and the two trials actually showed, then name what you would carry into the next design.",
        "tone": "mystery"
      },
      {
        "id": "reflect-prior-work",
        "type": "prior_work_summary",
        "title": "Review your evidence trail",
        "prompt": "Use your recorded work rather than relying only on memory.",
        "items": [
          {
            "source_step_id": "wind-car-wonder",
            "field": "initial_motion_theory",
            "label": "Opening theory"
          },
          {
            "source_step_id": "wind-car-test-improve",
            "field": "trial_comparison",
            "label": "Version 1 vs. Version 2 comparison"
          },
          {
            "source_step_id": "wind-car-test-improve",
            "field": "redesign_conclusion",
            "label": "Version 2 conclusion",
            "value_labels": {
              "improved": "Selected goal improved",
              "mixed": "Improvement with a tradeoff",
              "no_clear_change": "No clear improvement",
              "new_problem": "A new problem appeared",
              "unfair_test": "Comparison was not fair enough"
            }
          }
        ]
      },
      {
        "id": "final-reflection",
        "type": "evidence_response",
        "title": "Reflect on your thinking",
        "prompt": "What changed from your opening theory? What are you proud of, and what question do you still have?",
        "placeholder": "At first I thought... Now I know... I am proud that... I still wonder...",
        "save_as": "final_reflection",
        "min_length": 45,
        "required": true
      },
      {
        "id": "reflect-completion",
        "type": "completion_gate",
        "incomplete_title": "Complete your engineering reflection",
        "complete_title": "Reflect complete: evidence changed the story",
        "incomplete_prompt": "Explain what changed from your opening theory, what you are proud of, and what question remains.",
        "complete_prompt": "You identified how evidence changed your thinking. Next, Grow by connecting this rover to broader engineering ideas.",
        "rules": [
          {
            "field": "final_reflection",
            "condition": "min_length",
            "value": 45
          }
        ]
      }
    ]
  },
  "wind-car-grow": {
    "step_id": "wind-car-grow",
    "title": "Grow the Mission",
    "subtitle": "From Air-Push Rover to Version 3",
    "phase": "Grow",
    "story": "Transfer what you learned to other systems, then decide whether you want to continue independently with a Version 3 engineering challenge.",
    "self_guided": {
      "title": "Design the next fair rescue test",
      "say": "The most interesting engineering question may be the one you have not tested yet. Keep the motor and propeller safety rules for any future physical work.",
      "do_now": "Choose another rover challenge and describe the change you would test while keeping the comparison fair.",
      "help_prompt": "Professor Swan, guide me through this phase one action at a time, using the mission evidence and without giving me the answers or skipping safety checks.",
      "transition_summary": "You completed the guided rover mission by checking the key idea: batteries drive the motor and propeller, which push air to propel the rover. The extra Version 3 challenge is optional.",
      "next_preview": "Mission complete. Explain why the rover moves even though the motor is not driving the wheels."
    },
    "self_guided_block_guidance": {
      "honesty-check": {
        "title": "Compare the energy sources",
        "try_now": "Use the real-wind contrast to distinguish an outdoor wind turbine from this battery-powered rover."
      }
    },
    "blocks": [
      {
        "id": "grow-story",
        "type": "story_hook",
        "eyebrow": "GROW",
        "title": "Connect the rescue rover to bigger engineering ideas",
        "body": "The kit you built uses batteries to create moving air. Compare that mechanism with a real wind-powered system, then use your Version 3 idea to decide where your engineering could go next.",
        "tone": "mystery"
      },
      {
        "id": "real-wind-video-block",
        "type": "video_asset",
        "title": "Optional Contrast: How a Real Wind Turbine Works",
        "mission": "Use only as enrichment. Compare environmental wind turning turbine blades with batteries turning the rover propeller.",
        "asset": {
          "id": "real-wind-video",
          "type": "saras_video",
          "asset_ref": {
            "source_video_id": 673,
            "tag_id": 2461
          },
          "provenance": {
            "source_type": "collection",
            "collection_id": 168,
            "collection_item_id": 138
          },
          "content_context": {
            "base_board": "saras",
            "language_id": 1,
            "board_topic_id": 645,
            "source_topic_id": 496
          }
        }
      },
      {
        "id": "honesty-check",
        "type": "quiz_question",
        "title": "Scientific honesty check",
        "prompt": "Which description is accurate for the kit you built?",
        "choices": [
          {
            "id": "battery_air_push",
            "label": "Batteries power a motor and propeller that create moving air and thrust"
          },
          {
            "id": "outdoor_wind_motor",
            "label": "Outdoor wind turns the motor and charges the batteries"
          },
          {
            "id": "motor_wheels",
            "label": "The motor directly drives the wheels through hidden gears"
          },
          {
            "id": "renewable_only",
            "label": "The rover runs only on renewable wind energy"
          }
        ],
        "correct_answer": "battery_air_push",
        "save_as": "transfer_question",
        "correct_feedback": "Exactly. The kit creates airflow using battery energy. A wind turbine is a different system powered by environmental wind.",
        "incorrect_feedback": "Separate the kit name from the real mechanism you observed."
      },
      {
        "id": "grow-saras",
        "type": "saras_checkpoint",
        "title": "Stretch the idea with Saras",
        "suggested_questions": [
          "How is this battery-powered air-push rover different from a real wind turbine or sailboat?",
          "What makes a Version 3 comparison fair?",
          "Why are repeated trials stronger evidence than one lucky run?",
          "Challenge my next-test idea without writing the plan for me."
        ],
        "hidden_context": "The student is in the Grow phase after completing the guided Wind Powered Car explanation and reflection. Use transfer_question, engineers_rescue_report, and prior trial evidence. Reinforce that this kit begins with chemical energy in batteries, while real wind turbines and sailboats begin with environmental wind. Help the student think about fair comparisons, repeated trials, and a next variable, but do not author the optional YOU DO plan.",
        "reveal_policy": "The full rover science may be discussed. Support transfer and planning while preserving student ownership of the independent challenge."
      },
      {
        "id": "grow-completion",
        "type": "completion_gate",
        "incomplete_title": "Finish the transfer check",
        "complete_title": "Guided STEM Mission complete: Air-Push Rescue Engineer",
        "incomplete_prompt": "Use the evidence to distinguish this battery-powered air-push rover from a machine powered by environmental wind.",
        "complete_prompt": "You completed the nine-phase guided Mission. Your optional YOU DO Version 3 challenge continues below.",
        "rules": [
          {
            "field": "transfer_question",
            "condition": "equals",
            "value": "battery_air_push"
          }
        ]
      },
      {
        "id": "after-camp-green-valley-mission",
        "type": "story_hook",
        "eyebrow": "YOUR MISSION CONTINUES — YOU DO",
        "title": "Run the Green Valley Version 3 Challenge",
        "body": "Set aside about 60–90 minutes after the guided Mission. Work independently to improve the rover for a new rescue goal. Plan before changing anything, keep the power off during every adjustment, use a clear flat test lane, and collect evidence from three trials instead of trusting one lucky run.",
        "tone": "mystery",
        "instructional_mode": "you_do",
        "student_cue": "Complete this challenge independently after the guided STEM Mission. Your guided Mission is complete; this is your next engineering challenge."
      },
      {
        "id": "after-camp-green-valley-choice",
        "type": "choice_cards",
        "title": "Choose your Version 3 rescue mission",
        "prompt": "Choose one goal that will require a fair test, repeated trials, and evidence. You may do more than one if time allows.",
        "choices": [
          {
            "id": "straight_line",
            "label": "Straight-line accuracy — keep the rover inside a narrower rescue lane"
          },
          {
            "id": "delivery_reliability",
            "label": "Delivery reliability — complete three successful lightweight-package deliveries in a row"
          },
          {
            "id": "distance",
            "label": "Useful-distance challenge — improve travel distance while keeping the rover controlled"
          },
          {
            "id": "stability",
            "label": "Stability challenge — reduce veering, wheel rubbing, wobbling, or propeller interference"
          }
        ],
        "save_as": "after_camp_green_valley_choice",
        "required": false,
        "instructional_mode": "you_do",
        "student_cue": "Select the mission you will own from planning through evidence and recommendation."
      },
      {
        "id": "after-camp-green-valley-plan",
        "type": "experiment_record",
        "title": "Write the Version 3 test plan",
        "prompt": "A fair engineering test changes one main variable, keeps other conditions similar, and decides in advance what evidence will count as success.",
        "save_as": "after_camp_green_valley_plan",
        "required": false,
        "instructional_mode": "you_do",
        "student_cue": "Complete the plan in your own words before adjusting the rover.",
        "fields": [
          {
            "id": "goal",
            "label": "My rescue goal",
            "type": "text",
            "placeholder": "My Version 3 goal is to...",
            "min_length": 20
          },
          {
            "id": "one_change",
            "label": "The one main variable I will change or investigate",
            "type": "text",
            "placeholder": "I will change or investigate...",
            "min_length": 20
          },
          {
            "id": "keep_same",
            "label": "What I will keep the same for a fair comparison",
            "type": "text",
            "placeholder": "I will keep... the same because...",
            "min_length": 25
          },
          {
            "id": "prediction",
            "label": "My prediction and reason",
            "type": "text",
            "placeholder": "I predict... because...",
            "min_length": 30
          },
          {
            "id": "success_evidence",
            "label": "How I will measure success",
            "type": "text",
            "placeholder": "I will measure... I will count the mission as successful when...",
            "min_length": 30
          }
        ]
      },
      {
        "id": "after-camp-green-valley-safety",
        "type": "multi_select",
        "title": "Complete the independent rover safety check",
        "prompt": "Check each statement only when you are ready to follow it during every trial.",
        "choices": [
          {
            "id": "power_off",
            "label": "I will switch the battery holder OFF before touching or adjusting the rover"
          },
          {
            "id": "clear_propeller",
            "label": "I will keep fingers, hair, loose clothing, and objects away from the propeller"
          },
          {
            "id": "clear_lane",
            "label": "I will test only on a clear, flat lane away from stairs, traffic, pets, and fragile objects"
          },
          {
            "id": "light_payload",
            "label": "I will use only a lightweight package and will not overload the rover"
          },
          {
            "id": "approved_parts",
            "label": "I will use only the supplied or teacher-approved parts and batteries"
          }
        ],
        "save_as": "after_camp_green_valley_safety",
        "min_selections": 5,
        "required": false,
        "instructional_mode": "you_do",
        "student_cue": "Take responsibility for safe testing before you begin the three trials."
      },
      {
        "id": "after-camp-green-valley-trials",
        "type": "experiment_record",
        "title": "Run and record three Version 3 trials",
        "prompt": "Use the same success rule each time. Record distance, time, lane accuracy, delivery success, and visible problems when those measurements fit your mission.",
        "save_as": "after_camp_green_valley_trials",
        "required": false,
        "instructional_mode": "you_do",
        "student_cue": "Run three trials under similar conditions and make only power-off adjustments between trials.",
        "fields": [
          {
            "id": "trial_one",
            "label": "Trial 1 — setup, result, measurement, and visible evidence",
            "type": "text",
            "placeholder": "In Trial 1, the rover... It traveled... The package... I observed...",
            "min_length": 40
          },
          {
            "id": "between_one_two",
            "label": "Power-off adjustment before Trial 2, or why no adjustment was needed",
            "type": "text",
            "placeholder": "Before Trial 2, I switched off the power and...",
            "min_length": 25
          },
          {
            "id": "trial_two",
            "label": "Trial 2 — setup, result, measurement, and visible evidence",
            "type": "text",
            "placeholder": "In Trial 2, the rover... It traveled... The package... I observed...",
            "min_length": 40
          },
          {
            "id": "between_two_three",
            "label": "Power-off adjustment before Trial 3, or why no adjustment was needed",
            "type": "text",
            "placeholder": "Before Trial 3, I switched off the power and...",
            "min_length": 25
          },
          {
            "id": "trial_three",
            "label": "Trial 3 — setup, result, measurement, and visible evidence",
            "type": "text",
            "placeholder": "In Trial 3, the rover... It traveled... The package... I observed...",
            "min_length": 40
          },
          {
            "id": "repeatability",
            "label": "What was consistent or inconsistent across all three trials?",
            "type": "text",
            "placeholder": "Across the three trials, I noticed...",
            "min_length": 35
          }
        ]
      },
      {
        "id": "after-camp-green-valley-conclusion",
        "type": "evidence_response",
        "title": "Send Mission Control your Version 3 recommendation",
        "prompt": "Compare all three trials. Explain whether Version 3 improved the mission goal, created a tradeoff, or needs more evidence. Cite measurements or visible observations and explain what you would test next.",
        "placeholder": "Mission Control should... Across the three trials... The strongest evidence was... One tradeoff was... My next test would be...",
        "save_as": "after_camp_green_valley_conclusion",
        "min_length": 90,
        "required": false,
        "instructional_mode": "you_do",
        "student_cue": "Use evidence from all three trials to make an honest engineering recommendation."
      },
      {
        "id": "after-camp-green-valley-saras",
        "type": "saras_checkpoint",
        "title": "Challenge your Version 3 recommendation with Saras",
        "suggested_questions": [
          "Ask me to defend my recommendation using all three trials.",
          "Did I really change only one main variable?",
          "Were my test conditions similar enough for a fair comparison?",
          "What evidence best shows reliability rather than one lucky run?"
        ],
        "hidden_context": "The student is completing the optional Green Valley Version 3 YOU DO challenge after the guided nine-phase STEM Mission. Use after_camp_green_valley_choice, after_camp_green_valley_plan, after_camp_green_valley_safety, after_camp_green_valley_trials, and after_camp_green_valley_conclusion. Ask for specific evidence from all three trials. Check whether the student changed one main variable and kept the lane, payload, starting point, battery condition, and success rule reasonably consistent. Reinforce switching power off before adjustments, keeping clear of the propeller, using a lightweight payload, and testing on a clear flat lane. Coach the student's reasoning without rewriting the recommendation.",
        "reveal_policy": "The full rover science may be discussed. Emphasize fair comparison, repeatability, safety, evidence, and honest limits on the conclusion.",
        "instructional_mode": "you_do",
        "student_cue": "Use Saras as a coach after you have written your own recommendation."
      }
    ]
  }
};

export const getWindPoweredCarMissionPage = (stepId) =>
  WIND_POWERED_CAR_MISSION_PAGES[String(stepId || "")] || null;

export default WIND_POWERED_CAR_MISSION_PAGES;
